import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getConcept, getUnit } from '../data/units';
import { SIMULATION_GUIDE } from '../data/simulationGuide';
import styled from 'styled-components';

/* ─── Constants ────────────────────────────────────────────────────────────── */

const G = 9.8;
const CANVAS_W = 600;
const CANVAS_H = 400;

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

const toRad = (deg: number) => (deg * Math.PI) / 180;

const formatNum = (n: number, decimals = 2) =>
  Number.isFinite(n) ? n.toFixed(decimals) : '—';

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  INDIVIDUAL SIMULATION COMPONENTS                                         */
/* ═══════════════════════════════════════════════════════════════════════════ */

/* ─── 1. Projectile Motion ─────────────────────────────────────────────────── */

const ProjectileMotionSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [angle, setAngle] = useState(45);
  const [v0, setV0] = useState(20);
  const [running, setRunning] = useState(false);
  const [t, setT] = useState(0);
  const [trail, setTrail] = useState<{ x: number; y: number }[]>([]);
  const [info, setInfo] = useState({ maxH: 0, range: 0, tFlight: 0 });

  // Physics scale: 1 m = 8 px, origin at (60, 350)
  const SCALE = 8;
  const OX = 60;
  const OY = 350;

  const toScreen = useCallback(
    (mx: number, my: number) => ({ sx: OX + mx * SCALE, sy: OY - my * SCALE }),
    [],
  );

  // Pre-compute theoretical values
  useEffect(() => {
    const vx = v0 * Math.cos(toRad(angle));
    const vy = v0 * Math.sin(toRad(angle));
    const tFlight = (2 * vy) / G;
    const maxH = (vy * vy) / (2 * G);
    const range = vx * tFlight;
    setInfo({ maxH, range, tFlight });
  }, [angle, v0]);

  // Draw static scene + trail
  const drawScene = useCallback(
    (ctx: CanvasRenderingContext2D, currentTrail: { x: number; y: number }[], ballPos?: { x: number; y: number }) => {
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Ground
      ctx.fillStyle = '#e8f5e9';
      ctx.fillRect(0, OY, CANVAS_W, CANVAS_H - OY);
      ctx.strokeStyle = '#66bb6a';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, OY);
      ctx.lineTo(CANVAS_W, OY);
      ctx.stroke();

      // Axis ticks
      ctx.fillStyle = '#999';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      for (let m = 0; m <= 60; m += 10) {
        const sx = OX + m * SCALE;
        if (sx > CANVAS_W - 10) break;
        ctx.beginPath();
        ctx.moveTo(sx, OY);
        ctx.lineTo(sx, OY + 5);
        ctx.stroke();
        ctx.fillText(`${m}m`, sx, OY + 16);
      }
      ctx.textAlign = 'right';
      for (let m = 0; m <= 40; m += 10) {
        const sy = OY - m * SCALE;
        if (sy < 10) break;
        ctx.beginPath();
        ctx.moveTo(OX - 5, sy);
        ctx.lineTo(OX, sy);
        ctx.stroke();
        ctx.fillText(`${m}m`, OX - 8, sy + 3);
      }

      // Launcher
      const launcherLen = 30;
      const lx = OX + launcherLen * Math.cos(toRad(angle));
      const ly = OY - launcherLen * Math.sin(toRad(angle));
      ctx.strokeStyle = '#555';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(OX, OY);
      ctx.lineTo(lx, ly);
      ctx.stroke();

      // Base
      ctx.fillStyle = '#777';
      ctx.beginPath();
      ctx.arc(OX, OY, 6, 0, Math.PI * 2);
      ctx.fill();

      // Trail
      if (currentTrail.length > 1) {
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = accentColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        const first = toScreen(currentTrail[0].x, currentTrail[0].y);
        ctx.moveTo(first.sx, first.sy);
        for (let i = 1; i < currentTrail.length; i++) {
          const p = toScreen(currentTrail[i].x, currentTrail[i].y);
          ctx.lineTo(p.sx, p.sy);
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Ball
      if (ballPos) {
        const sp = toScreen(ballPos.x, ballPos.y);
        ctx.fillStyle = accentColor;
        ctx.beginPath();
        ctx.arc(sp.sx, sp.sy, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    },
    [angle, accentColor, toScreen],
  );

  // Initial draw
  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) drawScene(ctx, []);
  }, [drawScene]);

  const launch = useCallback(() => {
    setRunning(true);
    setTrail([]);
    setT(0);
    const vx = v0 * Math.cos(toRad(angle));
    const vy = v0 * Math.sin(toRad(angle));
    const tFlight = (2 * vy) / G;
    let startTime: number | null = null;
    const newTrail: { x: number; y: number }[] = [];

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000; // real seconds
      const simT = Math.min(elapsed * 1.5, tFlight); // speed up 1.5x

      const px = vx * simT;
      const py = vy * simT - 0.5 * G * simT * simT;

      newTrail.push({ x: px, y: Math.max(py, 0) });

      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) drawScene(ctx, newTrail, { x: px, y: Math.max(py, 0) });
      setT(simT);
      setTrail([...newTrail]);

      if (simT < tFlight) {
        animRef.current = requestAnimationFrame(step);
      } else {
        setRunning(false);
      }
    };

    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [angle, v0, drawScene]);

  const reset = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setT(0);
    setTrail([]);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) drawScene(ctx, []);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper>
        <StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} />
      </CanvasWrapper>

      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Launch Angle: {angle}&deg;</ControlLabel>
          <StyledSlider
            type="range" min={0} max={90} value={angle}
            onChange={(e) => { setAngle(+e.target.value); if (!running) reset(); }}
            $accent={accentColor}
          />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Initial Velocity: {v0} m/s</ControlLabel>
          <StyledSlider
            type="range" min={5} max={30} step={0.5} value={v0}
            onChange={(e) => { setV0(+e.target.value); if (!running) reset(); }}
            $accent={accentColor}
          />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={launch} disabled={running}>Launch</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={reset}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>

      <InfoPanel>
        <InfoItem><InfoLabel>Time</InfoLabel><InfoValue>{formatNum(t)} s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Max Height</InfoLabel><InfoValue>{formatNum(info.maxH)} m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Range</InfoLabel><InfoValue>{formatNum(info.range)} m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Total Flight Time</InfoLabel><InfoValue>{formatNum(info.tFlight)} s</InfoValue></InfoItem>
      </InfoPanel>

      <Instructions>
        Adjust the launch angle and initial velocity, then press <strong>Launch</strong> to fire the projectile.
        Watch the parabolic trajectory and observe how angle affects range and height. A 45-degree angle gives maximum range on flat ground.
      </Instructions>
    </>
  );
};

/* ─── 2. Free Fall ─────────────────────────────────────────────────────────── */

const FreeFallSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [height, setHeight] = useState(20);
  const [running, setRunning] = useState(false);
  const [currentY, setCurrentY] = useState(0);
  const [currentV, setCurrentV] = useState(0);
  const [currentT, setCurrentT] = useState(0);
  const [landed, setLanded] = useState(false);

  const SCALE = (CANVAS_H - 80) / 50; // max 50 m fits in canvas
  const GROUND_Y = CANVAS_H - 40;
  const BALL_X = CANVAS_W / 2;

  const drawScene = useCallback(
    (ballHeightM: number, velocityM: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Sky gradient
      const grad = ctx.createLinearGradient(0, 0, 0, GROUND_Y);
      grad.addColorStop(0, '#e3f2fd');
      grad.addColorStop(1, '#bbdefb');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, CANVAS_W, GROUND_Y);

      // Ground
      ctx.fillStyle = '#a5d6a7';
      ctx.fillRect(0, GROUND_Y, CANVAS_W, CANVAS_H - GROUND_Y);
      ctx.strokeStyle = '#66bb6a';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y);
      ctx.lineTo(CANVAS_W, GROUND_Y);
      ctx.stroke();

      // Height scale
      ctx.fillStyle = '#999';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      for (let m = 0; m <= 50; m += 10) {
        const sy = GROUND_Y - m * SCALE;
        if (sy < 10) break;
        ctx.beginPath();
        ctx.moveTo(BALL_X - 40, sy);
        ctx.lineTo(BALL_X - 35, sy);
        ctx.strokeStyle = '#bbb';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillText(`${m}m`, BALL_X - 44, sy + 3);
      }

      // Drop height marker
      const dropScreenY = GROUND_Y - height * SCALE;
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(BALL_X - 30, dropScreenY);
      ctx.lineTo(BALL_X + 30, dropScreenY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Ball
      const ballScreenY = GROUND_Y - ballHeightM * SCALE;
      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.arc(BALL_X, ballScreenY, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Velocity arrow
      if (velocityM > 0.5) {
        const arrowLen = Math.min(velocityM * 3, 80);
        ctx.strokeStyle = '#F44336';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(BALL_X + 24, ballScreenY);
        ctx.lineTo(BALL_X + 24, ballScreenY + arrowLen);
        ctx.stroke();
        // Arrowhead
        ctx.fillStyle = '#F44336';
        ctx.beginPath();
        ctx.moveTo(BALL_X + 24, ballScreenY + arrowLen + 6);
        ctx.lineTo(BALL_X + 18, ballScreenY + arrowLen - 4);
        ctx.lineTo(BALL_X + 30, ballScreenY + arrowLen - 4);
        ctx.closePath();
        ctx.fill();
        // Label
        ctx.fillStyle = '#F44336';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(`v = ${formatNum(velocityM, 1)} m/s`, BALL_X + 36, ballScreenY + arrowLen / 2 + 4);
      }
    },
    [height, accentColor, SCALE, GROUND_Y],
  );

  useEffect(() => {
    drawScene(height, 0);
  }, [height, drawScene]);

  const drop = useCallback(() => {
    setRunning(true);
    setLanded(false);
    setCurrentT(0);
    setCurrentV(0);
    setCurrentY(height);

    const tTotal = Math.sqrt((2 * height) / G);
    let startTime: number | null = null;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      const simT = Math.min(elapsed * 1.2, tTotal);

      const fallen = 0.5 * G * simT * simT;
      const y = Math.max(height - fallen, 0);
      const v = G * simT;

      setCurrentY(y);
      setCurrentV(v);
      setCurrentT(simT);
      drawScene(y, v);

      if (simT < tTotal) {
        animRef.current = requestAnimationFrame(step);
      } else {
        setRunning(false);
        setLanded(true);
        setCurrentY(0);
        const vFinal = Math.sqrt(2 * G * height);
        setCurrentV(vFinal);
        setCurrentT(tTotal);
        drawScene(0, vFinal);
      }
    };

    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [height, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setLanded(false);
    setCurrentT(0);
    setCurrentV(0);
    setCurrentY(height);
    drawScene(height, 0);
  }, [height, drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  const tTheory = Math.sqrt((2 * height) / G);
  const vTheory = Math.sqrt(2 * G * height);

  return (
    <>
      <CanvasWrapper>
        <StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} />
      </CanvasWrapper>

      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Drop Height: {height} m</ControlLabel>
          <StyledSlider
            type="range" min={1} max={50} value={height}
            onChange={(e) => { setHeight(+e.target.value); if (!running) { setCurrentY(+e.target.value); } }}
            $accent={accentColor}
          />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={drop} disabled={running}>Drop</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>

      <InfoPanel>
        <InfoItem><InfoLabel>Time</InfoLabel><InfoValue>{formatNum(currentT)} s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Current Height</InfoLabel><InfoValue>{formatNum(currentY)} m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Current Velocity</InfoLabel><InfoValue>{formatNum(currentV)} m/s</InfoValue></InfoItem>
        {landed && <InfoItem><InfoLabel>Final Velocity</InfoLabel><InfoValue style={{ color: '#F44336', fontWeight: 700 }}>{formatNum(vTheory)} m/s</InfoValue></InfoItem>}
        <InfoItem><InfoLabel>Theoretical Time</InfoLabel><InfoValue>{formatNum(tTheory)} s</InfoValue></InfoItem>
      </InfoPanel>

      <Instructions>
        Set the drop height and press <strong>Drop</strong>. The ball accelerates at g = 9.8 m/s&sup2;. Notice how the velocity increases linearly with time and the distance fallen grows quadratically.
      </Instructions>
    </>
  );
};

/* ─── 3. Newton's Second Law ───────────────────────────────────────────────── */

const NewtonsSecondLawSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [force, setForce] = useState(30);
  const [mass, setMass] = useState(5);
  const [running, setRunning] = useState(false);
  const [boxX, setBoxX] = useState(80);
  const [velocity, setVelocity] = useState(0);
  const [elapsedT, setElapsedT] = useState(0);

  const acceleration = force / mass;
  const BOX_W = 60;
  const BOX_H = 50;
  const GROUND_Y = CANVAS_H - 60;

  const drawScene = useCallback(
    (bx: number, vel: number, acc: number, appliedF: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Ground
      ctx.fillStyle = '#eceff1';
      ctx.fillRect(0, GROUND_Y, CANVAS_W, CANVAS_H - GROUND_Y);
      ctx.strokeStyle = '#90a4ae';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y);
      ctx.lineTo(CANVAS_W, GROUND_Y);
      ctx.stroke();

      // Hatching for ground
      ctx.strokeStyle = '#b0bec5';
      ctx.lineWidth = 1;
      for (let i = 0; i < CANVAS_W; i += 15) {
        ctx.beginPath();
        ctx.moveTo(i, GROUND_Y + 2);
        ctx.lineTo(i + 10, CANVAS_H);
        ctx.stroke();
      }

      // Box
      const bxClamped = Math.max(10, Math.min(bx, CANVAS_W - BOX_W - 10));
      const by = GROUND_Y - BOX_H;
      ctx.fillStyle = accentColor;
      ctx.fillRect(bxClamped, by, BOX_W, BOX_H);
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 2;
      ctx.strokeRect(bxClamped, by, BOX_W, BOX_H);

      // Mass label on box
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${mass} kg`, bxClamped + BOX_W / 2, by + BOX_H / 2 + 5);

      // Force arrow
      if (appliedF > 0) {
        const arrowLen = Math.min(appliedF * 1.5, 120);
        const arrowY = by + BOX_H / 2;
        const arrowStartX = bxClamped - 8;

        ctx.strokeStyle = '#F44336';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(arrowStartX - arrowLen, arrowY);
        ctx.lineTo(arrowStartX, arrowY);
        ctx.stroke();

        // Arrowhead
        ctx.fillStyle = '#F44336';
        ctx.beginPath();
        ctx.moveTo(arrowStartX + 8, arrowY);
        ctx.lineTo(arrowStartX - 6, arrowY - 8);
        ctx.lineTo(arrowStartX - 6, arrowY + 8);
        ctx.closePath();
        ctx.fill();

        // Force label
        ctx.fillStyle = '#F44336';
        ctx.font = 'bold 13px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`F = ${appliedF} N`, arrowStartX - arrowLen / 2, arrowY - 14);
      }

      // Acceleration arrow (green, above box)
      if (acc > 0) {
        const accArrowLen = Math.min(acc * 8, 80);
        const arrowY = by - 20;
        ctx.strokeStyle = '#4CAF50';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(bxClamped + BOX_W / 2, arrowY);
        ctx.lineTo(bxClamped + BOX_W / 2 + accArrowLen, arrowY);
        ctx.stroke();
        ctx.fillStyle = '#4CAF50';
        ctx.beginPath();
        ctx.moveTo(bxClamped + BOX_W / 2 + accArrowLen + 6, arrowY);
        ctx.lineTo(bxClamped + BOX_W / 2 + accArrowLen - 4, arrowY - 6);
        ctx.lineTo(bxClamped + BOX_W / 2 + accArrowLen - 4, arrowY + 6);
        ctx.closePath();
        ctx.fill();
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`a = ${formatNum(acc, 1)} m/s²`, bxClamped + BOX_W / 2 + accArrowLen / 2, arrowY - 10);
      }

      // Equation banner
      ctx.fillStyle = '#37474f';
      ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`F = m × a  →  ${appliedF} = ${mass} × ${formatNum(acc, 2)}`, CANVAS_W / 2, 35);
    },
    [mass, accentColor],
  );

  useEffect(() => {
    drawScene(boxX, 0, acceleration, force);
  }, [force, mass, drawScene, boxX, acceleration]);

  const startSim = useCallback(() => {
    setRunning(true);
    setBoxX(80);
    setVelocity(0);
    setElapsedT(0);

    const acc = force / mass;
    let startTime: number | null = null;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const dt = (timestamp - startTime) / 1000;
      const simT = dt * 0.8; // slow it a bit

      const v = acc * simT;
      const x = 80 + 0.5 * acc * simT * simT * 15; // 15 px per meter

      setBoxX(x);
      setVelocity(v);
      setElapsedT(simT);
      drawScene(x, v, acc, force);

      if (x < CANVAS_W - BOX_W - 10) {
        animRef.current = requestAnimationFrame(step);
      } else {
        setRunning(false);
      }
    };

    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [force, mass, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setBoxX(80);
    setVelocity(0);
    setElapsedT(0);
    drawScene(80, 0, force / mass, force);
  }, [force, mass, drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper>
        <StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} />
      </CanvasWrapper>

      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Applied Force: {force} N</ControlLabel>
          <StyledSlider
            type="range" min={0} max={100} value={force}
            onChange={(e) => { setForce(+e.target.value); if (!running) resetSim(); }}
            $accent={accentColor}
          />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Mass: {mass} kg</ControlLabel>
          <StyledSlider
            type="range" min={1} max={20} value={mass}
            onChange={(e) => { setMass(+e.target.value); if (!running) resetSim(); }}
            $accent={accentColor}
          />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Apply Force</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>

      <InfoPanel>
        <InfoItem><InfoLabel>Force (F)</InfoLabel><InfoValue>{force} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Mass (m)</InfoLabel><InfoValue>{mass} kg</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Acceleration (a = F/m)</InfoLabel><InfoValue>{formatNum(acceleration)} m/s&sup2;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Current Velocity</InfoLabel><InfoValue>{formatNum(velocity)} m/s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Time</InfoLabel><InfoValue>{formatNum(elapsedT)} s</InfoValue></InfoItem>
      </InfoPanel>

      <Instructions>
        Adjust the applied force and mass to see how acceleration changes according to F = ma. A larger force produces more acceleration; a larger mass produces less. Press <strong>Apply Force</strong> to watch the box accelerate.
      </Instructions>
    </>
  );
};

/* ─── 4. Friction ──────────────────────────────────────────────────────────── */

const FrictionSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [appliedForce, setAppliedForce] = useState(20);
  const [mass, setMass] = useState(5);
  const [mu, setMu] = useState(0.4);
  const [running, setRunning] = useState(false);
  const [boxX, setBoxX] = useState(100);
  const [elapsedT, setElapsedT] = useState(0);

  const normalForce = mass * G;
  const maxStaticFriction = mu * normalForce;
  const kineticFriction = mu * 0.85 * normalForce; // mu_k ~ 0.85 * mu_s
  const isMoving = appliedForce > maxStaticFriction;
  const netForce = isMoving ? appliedForce - kineticFriction : 0;
  const frictionForce = isMoving ? kineticFriction : Math.min(appliedForce, maxStaticFriction);
  const acc = isMoving ? netForce / mass : 0;

  const BOX_W = 60;
  const BOX_H = 50;
  const GROUND_Y = CANVAS_H - 80;

  const drawScene = useCallback(
    (bx: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Ground
      ctx.fillStyle = '#eceff1';
      ctx.fillRect(0, GROUND_Y, CANVAS_W, CANVAS_H - GROUND_Y);
      ctx.strokeStyle = '#90a4ae';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y);
      ctx.lineTo(CANVAS_W, GROUND_Y);
      ctx.stroke();

      // Rough surface indicator
      ctx.fillStyle = '#b0bec5';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`Surface: \u03BC = ${mu.toFixed(2)}`, CANVAS_W / 2, GROUND_Y + 20);

      // Dots for texture
      for (let i = 20; i < CANVAS_W - 20; i += 8) {
        ctx.fillStyle = '#cfd8dc';
        ctx.beginPath();
        ctx.arc(i, GROUND_Y + 30 + Math.random() * 10, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Box
      const bxClamped = Math.max(10, Math.min(bx, CANVAS_W - BOX_W - 10));
      const by = GROUND_Y - BOX_H;
      ctx.fillStyle = isMoving ? '#66bb6a' : accentColor;
      ctx.fillRect(bxClamped, by, BOX_W, BOX_H);
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 2;
      ctx.strokeRect(bxClamped, by, BOX_W, BOX_H);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${mass} kg`, bxClamped + BOX_W / 2, by + BOX_H / 2 + 5);

      const midY = by + BOX_H / 2;

      // Applied Force arrow (right, red)
      if (appliedForce > 0) {
        const aLen = Math.min(appliedForce * 1.2, 100);
        ctx.strokeStyle = '#F44336';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(bxClamped - 6, midY);
        ctx.lineTo(bxClamped - 6 - aLen, midY);
        ctx.stroke();
        ctx.fillStyle = '#F44336';
        ctx.beginPath();
        ctx.moveTo(bxClamped - 6, midY);
        ctx.lineTo(bxClamped - 14, midY - 6);
        ctx.lineTo(bxClamped - 14, midY + 6);
        ctx.closePath();
        ctx.fill();
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`F = ${appliedForce} N`, bxClamped - 6 - aLen / 2, midY - 12);
      }

      // Friction Force arrow (left, orange)
      if (frictionForce > 0.1) {
        const fLen = Math.min(frictionForce * 1.2, 100);
        ctx.strokeStyle = '#FF9800';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(bxClamped + BOX_W + 6, midY);
        ctx.lineTo(bxClamped + BOX_W + 6 + fLen, midY);
        ctx.stroke();
        ctx.fillStyle = '#FF9800';
        ctx.beginPath();
        ctx.moveTo(bxClamped + BOX_W + 6, midY);
        ctx.lineTo(bxClamped + BOX_W + 14, midY - 6);
        ctx.lineTo(bxClamped + BOX_W + 14, midY + 6);
        ctx.closePath();
        ctx.fill();
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`f = ${formatNum(frictionForce, 1)} N`, bxClamped + BOX_W + 6 + fLen / 2, midY - 12);
      }

      // Normal force arrow (up, blue)
      const nLen = Math.min(normalForce * 0.8, 70);
      ctx.strokeStyle = '#2196F3';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(bxClamped + BOX_W / 2, by - 5);
      ctx.lineTo(bxClamped + BOX_W / 2, by - 5 - nLen);
      ctx.stroke();
      ctx.fillStyle = '#2196F3';
      ctx.beginPath();
      ctx.moveTo(bxClamped + BOX_W / 2, by - 5 - nLen - 6);
      ctx.lineTo(bxClamped + BOX_W / 2 - 6, by - 5 - nLen + 4);
      ctx.lineTo(bxClamped + BOX_W / 2 + 6, by - 5 - nLen + 4);
      ctx.closePath();
      ctx.fill();
      ctx.font = 'bold 11px sans-serif';
      ctx.fillText(`N = ${formatNum(normalForce, 1)} N`, bxClamped + BOX_W / 2 + 40, by - 5 - nLen / 2);

      // Status text
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      if (isMoving) {
        ctx.fillStyle = '#4CAF50';
        ctx.fillText('Object is MOVING (kinetic friction)', CANVAS_W / 2, 30);
      } else {
        ctx.fillStyle = '#FF9800';
        ctx.fillText('Object is STATIONARY (static friction balances applied force)', CANVAS_W / 2, 30);
      }

      // Threshold indicator
      ctx.font = '12px sans-serif';
      ctx.fillStyle = '#666';
      ctx.fillText(`Static friction threshold: ${formatNum(maxStaticFriction, 1)} N`, CANVAS_W / 2, 52);
    },
    [appliedForce, mass, mu, isMoving, frictionForce, normalForce, maxStaticFriction, accentColor],
  );

  useEffect(() => {
    drawScene(boxX);
  }, [drawScene, boxX]);

  const startSim = useCallback(() => {
    if (!isMoving) return; // won't move
    setRunning(true);
    setBoxX(100);
    setElapsedT(0);
    let startTime: number | null = null;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const dt = (timestamp - startTime) / 1000;
      const simT = dt * 0.8;
      const x = 100 + 0.5 * acc * simT * simT * 12;
      setBoxX(x);
      setElapsedT(simT);
      drawScene(x);

      if (x < CANVAS_W - BOX_W - 10) {
        animRef.current = requestAnimationFrame(step);
      } else {
        setRunning(false);
      }
    };

    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [isMoving, acc, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setBoxX(100);
    setElapsedT(0);
    drawScene(100);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper>
        <StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} />
      </CanvasWrapper>

      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Applied Force: {appliedForce} N</ControlLabel>
          <StyledSlider
            type="range" min={0} max={100} value={appliedForce}
            onChange={(e) => { setAppliedForce(+e.target.value); if (!running) resetSim(); }}
            $accent={accentColor}
          />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Mass: {mass} kg</ControlLabel>
          <StyledSlider
            type="range" min={1} max={20} value={mass}
            onChange={(e) => { setMass(+e.target.value); if (!running) resetSim(); }}
            $accent={accentColor}
          />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Friction Coefficient (&mu;): {mu.toFixed(2)}</ControlLabel>
          <StyledSlider
            type="range" min={0} max={1} step={0.01} value={mu}
            onChange={(e) => { setMu(+e.target.value); if (!running) resetSim(); }}
            $accent={accentColor}
          />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running || !isMoving}>
            {isMoving ? 'Apply Force' : 'Force Too Small'}
          </SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>

      <InfoPanel>
        <InfoItem><InfoLabel>Applied Force</InfoLabel><InfoValue>{appliedForce} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Normal Force (mg)</InfoLabel><InfoValue>{formatNum(normalForce)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Max Static Friction (&mu;N)</InfoLabel><InfoValue>{formatNum(maxStaticFriction)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Friction Force</InfoLabel><InfoValue>{formatNum(frictionForce)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Net Force</InfoLabel><InfoValue>{formatNum(netForce)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Acceleration</InfoLabel><InfoValue>{formatNum(acc)} m/s&sup2;</InfoValue></InfoItem>
      </InfoPanel>

      <Instructions>
        Adjust the applied force, mass, and friction coefficient. The object will not move until the applied force exceeds the maximum static friction (&mu; &times; N). Once it starts moving, kinetic friction (slightly less than static) acts on it. Increase force or decrease &mu; to get the box moving.
      </Instructions>
    </>
  );
};

/* ─── 5. Conservation of Energy ────────────────────────────────────────────── */

const ConservationOfEnergySim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [running, setRunning] = useState(false);
  const [ballParam, setBallParam] = useState(0); // 0..1 parameter along the track

  // Track definition: a roller-coaster profile as a series of height points
  const TRACK_POINTS = 200;
  const trackHeight = useCallback((param: number): number => {
    // Hill 1: starts high, dip, hill, dip, ends low
    const x = param;
    return (
      120 * Math.sin(x * Math.PI * 0.5) * Math.exp(-x * 0.5) +
      50 * Math.sin(x * Math.PI * 2) * Math.exp(-x * 1.2) +
      20 * Math.sin(x * Math.PI * 4) * Math.exp(-x * 2) +
      10
    );
  }, []);

  const maxTrackHeight = (() => {
    let max = 0;
    for (let i = 0; i <= TRACK_POINTS; i++) {
      max = Math.max(max, trackHeight(i / TRACK_POINTS));
    }
    return max;
  })();

  const GROUND_Y = CANVAS_H - 100;
  const TRACK_X_START = 40;
  const TRACK_X_END = CANVAS_W - 40;
  const TRACK_SCALE_Y = 2.0;

  const getTrackScreen = useCallback(
    (param: number) => {
      const x = TRACK_X_START + param * (TRACK_X_END - TRACK_X_START);
      const h = trackHeight(param);
      const y = GROUND_Y - h * TRACK_SCALE_Y;
      return { x, y, h };
    },
    [trackHeight, GROUND_Y],
  );

  const drawScene = useCallback(
    (param: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Background
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Draw track
      ctx.strokeStyle = '#555';
      ctx.lineWidth = 3;
      ctx.beginPath();
      for (let i = 0; i <= TRACK_POINTS; i++) {
        const p = i / TRACK_POINTS;
        const pt = getTrackScreen(p);
        if (i === 0) ctx.moveTo(pt.x, pt.y);
        else ctx.lineTo(pt.x, pt.y);
      }
      ctx.stroke();

      // Track supports
      ctx.strokeStyle = '#bbb';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 20; i++) {
        const p = i / 20;
        const pt = getTrackScreen(p);
        ctx.beginPath();
        ctx.moveTo(pt.x, pt.y);
        ctx.lineTo(pt.x, GROUND_Y + 5);
        ctx.stroke();
      }

      // Ground line
      ctx.strokeStyle = '#90a4ae';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y + 5);
      ctx.lineTo(CANVAS_W, GROUND_Y + 5);
      ctx.stroke();

      // Ball position
      const ballPt = getTrackScreen(param);
      const ballH = trackHeight(param);
      const startH = trackHeight(0);
      const mass = 1; // 1 kg for simplicity
      const PE = mass * G * ballH;
      const totalE = mass * G * startH;
      const KE = Math.max(totalE - PE, 0);

      // Ball
      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.arc(ballPt.x, ballPt.y - 8, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Energy bar charts (right side)
      const barX = CANVAS_W - 130;
      const barW = 30;
      const barMaxH = 120;
      const barBaseY = GROUND_Y - 10;

      // PE bar
      const peH = (PE / totalE) * barMaxH;
      ctx.fillStyle = '#2196F3';
      ctx.fillRect(barX, barBaseY - peH, barW, peH);
      ctx.strokeStyle = '#1565C0';
      ctx.lineWidth = 1;
      ctx.strokeRect(barX, barBaseY - peH, barW, peH);
      ctx.fillStyle = '#333';
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('PE', barX + barW / 2, barBaseY + 14);
      ctx.fillStyle = '#2196F3';
      ctx.fillText(`${formatNum(PE, 0)} J`, barX + barW / 2, barBaseY - peH - 6);

      // KE bar
      const keH = (KE / totalE) * barMaxH;
      ctx.fillStyle = '#F44336';
      ctx.fillRect(barX + 40, barBaseY - keH, barW, keH);
      ctx.strokeStyle = '#C62828';
      ctx.lineWidth = 1;
      ctx.strokeRect(barX + 40, barBaseY - keH, barW, keH);
      ctx.fillStyle = '#333';
      ctx.fillText('KE', barX + 40 + barW / 2, barBaseY + 14);
      ctx.fillStyle = '#F44336';
      ctx.fillText(`${formatNum(KE, 0)} J`, barX + 40 + barW / 2, barBaseY - keH - 6);

      // Total E bar
      const teH = barMaxH;
      ctx.fillStyle = '#4CAF50';
      ctx.globalAlpha = 0.3;
      ctx.fillRect(barX + 80, barBaseY - teH, barW, teH);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = '#2E7D32';
      ctx.lineWidth = 1;
      ctx.strokeRect(barX + 80, barBaseY - teH, barW, teH);
      ctx.fillStyle = '#333';
      ctx.fillText('Total', barX + 80 + barW / 2, barBaseY + 14);
      ctx.fillStyle = '#4CAF50';
      ctx.fillText(`${formatNum(totalE, 0)} J`, barX + 80 + barW / 2, barBaseY - teH - 6);

      // Title
      ctx.fillStyle = '#333';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Energy Conservation: KE + PE = constant', CANVAS_W / 2, 25);
    },
    [accentColor, getTrackScreen, trackHeight],
  );

  useEffect(() => {
    drawScene(ballParam);
  }, [drawScene, ballParam]);

  const startSim = useCallback(() => {
    setRunning(true);
    setBallParam(0);

    // Simulate: use energy conservation to find velocity, then advance position
    // v = sqrt(2g(h0 - h)), ds/dt = v, so dp/dt = v / trackLength
    const startH = trackHeight(0);
    const totalTrackLen = (() => {
      let len = 0;
      for (let i = 1; i <= TRACK_POINTS; i++) {
        const p0 = getTrackScreen((i - 1) / TRACK_POINTS);
        const p1 = getTrackScreen(i / TRACK_POINTS);
        len += Math.sqrt((p1.x - p0.x) ** 2 + (p1.y - p0.y) ** 2);
      }
      return len;
    })();

    let param = 0;
    let lastTimestamp: number | null = null;

    const step = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const dtReal = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      const h = trackHeight(param);
      const v = Math.sqrt(Math.max(2 * G * (startH - h), 0));
      // dp = v * dt / totalTrackLenInMeters
      // Approximate track length in meters (scale): total pixels / TRACK_SCALE
      const trackLenM = totalTrackLen / (TRACK_SCALE_Y * 3);
      const dp = (v * dtReal * 2.5) / trackLenM; // speed factor
      param = Math.min(param + dp, 1);

      setBallParam(param);
      drawScene(param);

      if (param < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        setRunning(false);
      }
    };

    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [trackHeight, getTrackScreen, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setBallParam(0);
    drawScene(0);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  const currentH = trackHeight(ballParam);
  const startH = trackHeight(0);
  const PE = G * currentH;
  const KE = Math.max(G * (startH - currentH), 0);

  return (
    <>
      <CanvasWrapper>
        <StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} />
      </CanvasWrapper>

      <ControlsPanel>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Release Ball</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>

      <InfoPanel>
        <InfoItem><InfoLabel>Height</InfoLabel><InfoValue>{formatNum(currentH)} m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Potential Energy (mgh)</InfoLabel><InfoValue>{formatNum(PE)} J/kg</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Kinetic Energy</InfoLabel><InfoValue>{formatNum(KE)} J/kg</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Total Energy</InfoLabel><InfoValue>{formatNum(G * startH)} J/kg</InfoValue></InfoItem>
      </InfoPanel>

      <Instructions>
        Press <strong>Release Ball</strong> to let the ball roll along the track under gravity. Watch how potential energy converts to kinetic energy (and vice versa) while the total energy stays constant. The bar chart on the right shows this exchange in real time.
      </Instructions>
    </>
  );
};

/* ─── 6. Collisions ────────────────────────────────────────────────────────── */

const CollisionsSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [m1, setM1] = useState(3);
  const [m2, setM2] = useState(3);
  const [v1, setV1] = useState(5);
  const [v2, setV2] = useState(-3);
  const [elastic, setElastic] = useState(true);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState<'before' | 'after'>('before');

  // Ball positions
  const [b1x, setB1x] = useState(120);
  const [b2x, setB2x] = useState(440);
  const [b1v, setB1v] = useState(0);
  const [b2v, setB2v] = useState(0);

  const GROUND_Y = CANVAS_H - 60;
  const ballRadius = (m: number) => 15 + m * 1.5;

  const pBefore = m1 * v1 + m2 * v2;
  const keBefore = 0.5 * m1 * v1 * v1 + 0.5 * m2 * v2 * v2;

  // Compute post-collision velocities
  const computePostCollision = useCallback(() => {
    if (elastic) {
      const v1f = ((m1 - m2) * v1 + 2 * m2 * v2) / (m1 + m2);
      const v2f = ((m2 - m1) * v2 + 2 * m1 * v1) / (m1 + m2);
      return { v1f, v2f };
    } else {
      const vf = (m1 * v1 + m2 * v2) / (m1 + m2);
      return { v1f: vf, v2f: vf };
    }
  }, [m1, m2, v1, v2, elastic]);

  const drawScene = useCallback(
    (x1: number, x2: number, vel1: number, vel2: number, currentPhase: string) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Background
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Ground
      ctx.fillStyle = '#eceff1';
      ctx.fillRect(0, GROUND_Y, CANVAS_W, CANVAS_H - GROUND_Y);
      ctx.strokeStyle = '#90a4ae';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y);
      ctx.lineTo(CANVAS_W, GROUND_Y);
      ctx.stroke();

      const r1 = ballRadius(m1);
      const r2 = ballRadius(m2);
      const y1 = GROUND_Y - r1;
      const y2 = GROUND_Y - r2;

      // Ball 1
      ctx.fillStyle = '#2196F3';
      ctx.beginPath();
      ctx.arc(x1, y1, r1, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#1565C0';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${m1}kg`, x1, y1 + 4);

      // Ball 2
      ctx.fillStyle = '#F44336';
      ctx.beginPath();
      ctx.arc(x2, y2, r2, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#C62828';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.fillText(`${m2}kg`, x2, y2 + 4);

      // Velocity arrows
      const drawVelArrow = (cx: number, cy: number, vel: number, color: string) => {
        if (Math.abs(vel) < 0.1) return;
        const arrowLen = vel * 10;
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(cx, cy - r1 - 15);
        ctx.lineTo(cx + arrowLen, cy - r1 - 15);
        ctx.stroke();
        const dir = vel > 0 ? 1 : -1;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(cx + arrowLen + 6 * dir, cy - r1 - 15);
        ctx.lineTo(cx + arrowLen - 4 * dir, cy - r1 - 21);
        ctx.lineTo(cx + arrowLen - 4 * dir, cy - r1 - 9);
        ctx.closePath();
        ctx.fill();
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`${formatNum(vel, 1)} m/s`, cx + arrowLen / 2, cy - r1 - 22);
      };

      drawVelArrow(x1, y1, vel1, '#1565C0');
      drawVelArrow(x2, y2, vel2, '#C62828');

      // Momentum display
      const pAfter = m1 * vel1 + m2 * vel2;
      ctx.fillStyle = '#333';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(
        `Momentum: p = ${formatNum(pBefore, 1)} kg\u00B7m/s  (${currentPhase === 'after' ? 'conserved!' : 'before collision'})`,
        CANVAS_W / 2, 25,
      );
      ctx.font = '12px sans-serif';
      ctx.fillText(
        `Type: ${elastic ? 'Elastic (KE conserved)' : 'Perfectly Inelastic (objects stick together)'}`,
        CANVAS_W / 2, 45,
      );

      if (currentPhase === 'after') {
        const keAfter = 0.5 * m1 * vel1 * vel1 + 0.5 * m2 * vel2 * vel2;
        ctx.fillText(
          `KE before: ${formatNum(keBefore, 1)} J  |  KE after: ${formatNum(keAfter, 1)} J`,
          CANVAS_W / 2, 65,
        );
      }
    },
    [m1, m2, elastic, pBefore, keBefore],
  );

  // Initial draw
  useEffect(() => {
    setB1x(120);
    setB2x(440);
    setB1v(v1);
    setB2v(v2);
    setPhase('before');
    drawScene(120, 440, v1, v2, 'before');
  }, [m1, m2, v1, v2, elastic, drawScene]);

  const startSim = useCallback(() => {
    setRunning(true);
    setPhase('before');
    let x1 = 120;
    let x2 = 440;
    let cv1 = v1;
    let cv2 = v2;
    let collided = false;
    let lastTimestamp: number | null = null;

    const { v1f, v2f } = computePostCollision();

    const step = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const dt = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      x1 += cv1 * dt * 40;
      x2 += cv2 * dt * 40;

      const r1 = ballRadius(m1);
      const r2 = ballRadius(m2);

      // Check collision
      if (!collided && x1 + r1 >= x2 - r2) {
        collided = true;
        if (!elastic) {
          // Stick together — put them adjacent
          const combined = (x1 + x2) / 2;
          x1 = combined - 2;
          x2 = combined + 2;
        } else {
          // Push apart slightly
          const overlap = (x1 + r1) - (x2 - r2);
          x1 -= overlap / 2;
          x2 += overlap / 2;
        }
        cv1 = v1f;
        cv2 = v2f;
        setPhase('after');
      }

      if (elastic || !collided) {
        setB1x(x1);
        setB2x(x2);
      } else {
        // Inelastic: move together
        const mid = (x1 + x2) / 2;
        setB1x(mid - 2);
        setB2x(mid + 2);
        x1 = mid - 2;
        x2 = mid + 2;
      }

      setB1v(cv1);
      setB2v(cv2);
      drawScene(x1, x2, cv1, cv2, collided ? 'after' : 'before');

      // Stop if both off screen or 5 seconds after collision
      if (x1 > CANVAS_W + 50 || x2 < -50 || x1 < -50 || x2 > CANVAS_W + 50) {
        setRunning(false);
        return;
      }

      animRef.current = requestAnimationFrame(step);
    };

    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [v1, v2, m1, m2, elastic, computePostCollision, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setPhase('before');
    setB1x(120);
    setB2x(440);
    setB1v(v1);
    setB2v(v2);
    drawScene(120, 440, v1, v2, 'before');
  }, [v1, v2, drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  const { v1f, v2f } = computePostCollision();
  const keAfter = elastic
    ? 0.5 * m1 * v1f * v1f + 0.5 * m2 * v2f * v2f
    : 0.5 * (m1 + m2) * v1f * v1f;

  return (
    <>
      <CanvasWrapper>
        <StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} />
      </CanvasWrapper>

      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Mass 1 (blue): {m1} kg</ControlLabel>
          <StyledSlider type="range" min={1} max={10} value={m1} onChange={(e) => setM1(+e.target.value)} $accent="#2196F3" />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Velocity 1: {v1} m/s</ControlLabel>
          <StyledSlider type="range" min={-10} max={10} value={v1} onChange={(e) => setV1(+e.target.value)} $accent="#2196F3" />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Mass 2 (red): {m2} kg</ControlLabel>
          <StyledSlider type="range" min={1} max={10} value={m2} onChange={(e) => setM2(+e.target.value)} $accent="#F44336" />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Velocity 2: {v2} m/s</ControlLabel>
          <StyledSlider type="range" min={-10} max={10} value={v2} onChange={(e) => setV2(+e.target.value)} $accent="#F44336" />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Collision Type:</ControlLabel>
          <ToggleRow>
            <ToggleButton $active={elastic} $accent={accentColor} onClick={() => setElastic(true)}>Elastic</ToggleButton>
            <ToggleButton $active={!elastic} $accent={accentColor} onClick={() => setElastic(false)}>Perfectly Inelastic</ToggleButton>
          </ToggleRow>
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Collide!</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>

      <InfoPanel>
        <InfoItem><InfoLabel>Momentum Before</InfoLabel><InfoValue>{formatNum(pBefore)} kg&middot;m/s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Momentum After</InfoLabel><InfoValue>{formatNum(m1 * v1f + m2 * v2f)} kg&middot;m/s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>KE Before</InfoLabel><InfoValue>{formatNum(keBefore)} J</InfoValue></InfoItem>
        <InfoItem><InfoLabel>KE After</InfoLabel><InfoValue>{formatNum(keAfter)} J</InfoValue></InfoItem>
        <InfoItem><InfoLabel>v1 after</InfoLabel><InfoValue>{formatNum(v1f)} m/s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>v2 after</InfoLabel><InfoValue>{formatNum(v2f)} m/s</InfoValue></InfoItem>
      </InfoPanel>

      <Instructions>
        Set the masses and velocities of two balls, choose elastic or inelastic collision, then press <strong>Collide!</strong>. In elastic collisions, kinetic energy is conserved. In perfectly inelastic collisions, the objects stick together and KE is lost. Momentum is always conserved.
      </Instructions>
    </>
  );
};

/* ─── 7. Springs (SHM) ────────────────────────────────────────────────────── */

const SpringsSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const graphCanvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [k, setK] = useState(10);
  const [mass, setMass] = useState(1);
  const [running, setRunning] = useState(false);
  const [displacement, setDisplacement] = useState(0);
  const [simTime, setSimTime] = useState(0);

  const omega = Math.sqrt(k / mass);
  const period = (2 * Math.PI) / omega;
  const frequency = 1 / period;
  const amplitude = 80; // pixels
  const amplitudeM = 0.5; // meters

  const WALL_X = 40;
  const EQUIL_X = 280;
  const BLOCK_W = 50;
  const BLOCK_H = 40;
  const SPRING_Y = CANVAS_H / 2 - 30;

  const drawSpring = useCallback(
    (ctx: CanvasRenderingContext2D, x1: number, x2: number, y: number, coils: number) => {
      const dx = x2 - x1;
      const coilW = dx / (coils * 2);
      const coilH = 12;

      ctx.strokeStyle = '#666';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x1, y);

      for (let i = 0; i < coils * 2; i++) {
        const cx = x1 + coilW * (i + 0.5);
        const cy = i % 2 === 0 ? y - coilH : y + coilH;
        ctx.lineTo(cx, cy);
      }
      ctx.lineTo(x2, y);
      ctx.stroke();
    },
    [],
  );

  const drawScene = useCallback(
    (disp: number, time: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Background
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Wall
      ctx.fillStyle = '#78909c';
      ctx.fillRect(0, SPRING_Y - 40, WALL_X, 80);
      // Wall hatching
      ctx.strokeStyle = '#546e7a';
      ctx.lineWidth = 1;
      for (let i = -40; i < 80; i += 8) {
        ctx.beginPath();
        ctx.moveTo(0, SPRING_Y + i);
        ctx.lineTo(WALL_X - 5, SPRING_Y + i + 15);
        ctx.stroke();
      }

      // Equilibrium line
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = '#bbb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(EQUIL_X + BLOCK_W / 2, SPRING_Y - 50);
      ctx.lineTo(EQUIL_X + BLOCK_W / 2, SPRING_Y + 60);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#aaa';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('equilibrium', EQUIL_X + BLOCK_W / 2, SPRING_Y + 72);

      // Block position
      const blockX = EQUIL_X + disp;

      // Spring
      drawSpring(ctx, WALL_X, blockX, SPRING_Y, 15);

      // Ground line
      const groundY = SPRING_Y + BLOCK_H / 2 + 2;
      ctx.strokeStyle = '#90a4ae';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(WALL_X, groundY);
      ctx.lineTo(CANVAS_W, groundY);
      ctx.stroke();

      // Block
      ctx.fillStyle = accentColor;
      ctx.fillRect(blockX, SPRING_Y - BLOCK_H / 2, BLOCK_W, BLOCK_H);
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 2;
      ctx.strokeRect(blockX, SPRING_Y - BLOCK_H / 2, BLOCK_W, BLOCK_H);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${mass} kg`, blockX + BLOCK_W / 2, SPRING_Y + 5);

      // Force arrow
      const forceDisp = -disp * 0.6; // restoring force direction
      if (Math.abs(forceDisp) > 2) {
        ctx.strokeStyle = '#F44336';
        ctx.lineWidth = 3;
        const arrowY = SPRING_Y - BLOCK_H / 2 - 15;
        ctx.beginPath();
        ctx.moveTo(blockX + BLOCK_W / 2, arrowY);
        ctx.lineTo(blockX + BLOCK_W / 2 + forceDisp, arrowY);
        ctx.stroke();
        const dir = forceDisp > 0 ? 1 : -1;
        ctx.fillStyle = '#F44336';
        ctx.beginPath();
        ctx.moveTo(blockX + BLOCK_W / 2 + forceDisp + 6 * dir, arrowY);
        ctx.lineTo(blockX + BLOCK_W / 2 + forceDisp - 4 * dir, arrowY - 5);
        ctx.lineTo(blockX + BLOCK_W / 2 + forceDisp - 4 * dir, arrowY + 5);
        ctx.closePath();
        ctx.fill();
        ctx.font = '10px sans-serif';
        ctx.fillText('F = -kx', blockX + BLOCK_W / 2 + forceDisp / 2, arrowY - 8);
      }

      // Info text
      const dispM = (disp / amplitude) * amplitudeM;
      ctx.fillStyle = '#333';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`x = ${formatNum(dispM, 3)} m`, 20, 25);
      ctx.fillText(`T = ${formatNum(period)} s`, 20, 45);
      ctx.fillText(`f = ${formatNum(frequency)} Hz`, 180, 25);
      ctx.fillText(`\u03C9 = ${formatNum(omega)} rad/s`, 180, 45);

      // Position vs time graph (bottom part of canvas)
      const graphY = SPRING_Y + 80;
      const graphW = CANVAS_W - 80;
      const graphH = CANVAS_H - graphY - 20;

      // Graph axes
      ctx.strokeStyle = '#999';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(60, graphY);
      ctx.lineTo(60, graphY + graphH);
      ctx.lineTo(60 + graphW, graphY + graphH);
      ctx.stroke();

      // Graph zero line
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = '#ccc';
      ctx.beginPath();
      ctx.moveTo(60, graphY + graphH / 2);
      ctx.lineTo(60 + graphW, graphY + graphH / 2);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = '#999';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText('x(t)', 55, graphY + 10);
      ctx.fillText('+A', 55, graphY + 5);
      ctx.fillText('-A', 55, graphY + graphH);
      ctx.textAlign = 'center';
      ctx.fillText('time', 60 + graphW / 2, graphY + graphH + 14);

      // Plot x(t)
      if (time > 0) {
        const tWindow = Math.max(period * 3, 5);
        ctx.strokeStyle = accentColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        let started = false;
        for (let px = 0; px < graphW; px++) {
          const tPx = (px / graphW) * tWindow;
          if (tPx > time) break;
          const xVal = amplitudeM * Math.cos(omega * tPx);
          const gy = graphY + graphH / 2 - (xVal / amplitudeM) * (graphH / 2 - 5);
          if (!started) { ctx.moveTo(60 + px, gy); started = true; }
          else ctx.lineTo(60 + px, gy);
        }
        ctx.stroke();

        // Current position dot on graph
        const tNorm = (time / tWindow) * graphW;
        if (tNorm <= graphW) {
          const xNow = amplitudeM * Math.cos(omega * time);
          const gyNow = graphY + graphH / 2 - (xNow / amplitudeM) * (graphH / 2 - 5);
          ctx.fillStyle = accentColor;
          ctx.beginPath();
          ctx.arc(60 + tNorm, gyNow, 4, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    },
    [k, mass, omega, period, frequency, amplitude, amplitudeM, drawSpring, accentColor],
  );

  useEffect(() => {
    drawScene(0, 0);
  }, [drawScene]);

  const startSim = useCallback(() => {
    setRunning(true);
    setSimTime(0);
    let startTime: number | null = null;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;

      const disp = amplitude * Math.cos(omega * elapsed);
      setDisplacement(disp);
      setSimTime(elapsed);
      drawScene(disp, elapsed);

      // Run for ~3 periods or 10 seconds max
      if (elapsed < Math.max(period * 3, 10)) {
        animRef.current = requestAnimationFrame(step);
      } else {
        setRunning(false);
      }
    };

    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [omega, period, amplitude, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setDisplacement(0);
    setSimTime(0);
    drawScene(0, 0);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper>
        <StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} />
      </CanvasWrapper>

      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Spring Constant (k): {k} N/m</ControlLabel>
          <StyledSlider type="range" min={1} max={50} value={k} onChange={(e) => { setK(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Mass: {mass} kg</ControlLabel>
          <StyledSlider type="range" min={0.1} max={5} step={0.1} value={mass} onChange={(e) => { setMass(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Pull &amp; Release</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>

      <InfoPanel>
        <InfoItem><InfoLabel>Period (T = 2&pi;&radic;(m/k))</InfoLabel><InfoValue>{formatNum(period)} s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Frequency</InfoLabel><InfoValue>{formatNum(frequency)} Hz</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Angular Frequency (&omega;)</InfoLabel><InfoValue>{formatNum(omega)} rad/s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Displacement</InfoLabel><InfoValue>{formatNum((displacement / amplitude) * amplitudeM, 3)} m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Time</InfoLabel><InfoValue>{formatNum(simTime)} s</InfoValue></InfoItem>
      </InfoPanel>

      <Instructions>
        Adjust the spring constant (k) and mass, then press <strong>Pull &amp; Release</strong>. A stiffer spring (higher k) or lighter mass oscillates faster. The position vs time graph below shows the sinusoidal motion characteristic of simple harmonic motion.
      </Instructions>
    </>
  );
};

/* ─── 8. Pendulums (SHM) ──────────────────────────────────────────────────── */

const PendulumsSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [length, setLength] = useState(1.5);
  const [initAngle, setInitAngle] = useState(20);
  const [running, setRunning] = useState(false);
  const [currentAngle, setCurrentAngle] = useState(20);
  const [simTime, setSimTime] = useState(0);

  const omega = Math.sqrt(G / length);
  const period = (2 * Math.PI) / omega;

  const PIVOT_X = CANVAS_W / 2;
  const PIVOT_Y = 60;
  const PX_PER_M = 150; // pixels per meter of pendulum length
  const BOB_R = 16;

  const drawScene = useCallback(
    (angleDeg: number, time: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Background
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Ceiling
      ctx.fillStyle = '#78909c';
      ctx.fillRect(0, 0, CANVAS_W, PIVOT_Y - 10);
      ctx.strokeStyle = '#546e7a';
      ctx.lineWidth = 1;
      for (let i = 0; i < CANVAS_W; i += 12) {
        ctx.beginPath();
        ctx.moveTo(i, PIVOT_Y - 15);
        ctx.lineTo(i + 10, PIVOT_Y - 10);
        ctx.stroke();
      }

      // Vertical reference line
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PIVOT_X, PIVOT_Y);
      ctx.lineTo(PIVOT_X, PIVOT_Y + length * PX_PER_M + 30);
      ctx.stroke();
      ctx.setLineDash([]);

      // Pendulum string
      const angleRad = toRad(angleDeg);
      const bobX = PIVOT_X + length * PX_PER_M * Math.sin(angleRad);
      const bobY = PIVOT_Y + length * PX_PER_M * Math.cos(angleRad);

      ctx.strokeStyle = '#555';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(PIVOT_X, PIVOT_Y);
      ctx.lineTo(bobX, bobY);
      ctx.stroke();

      // Pivot
      ctx.fillStyle = '#333';
      ctx.beginPath();
      ctx.arc(PIVOT_X, PIVOT_Y, 5, 0, Math.PI * 2);
      ctx.fill();

      // Bob
      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.arc(bobX, bobY, BOB_R, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Angle arc
      if (Math.abs(angleDeg) > 1) {
        const arcR = 40;
        const startAngle = Math.PI / 2;
        ctx.strokeStyle = '#FF9800';
        ctx.lineWidth = 2;
        ctx.beginPath();
        if (angleDeg > 0) {
          ctx.arc(PIVOT_X, PIVOT_Y, arcR, startAngle - Math.abs(angleRad), startAngle);
        } else {
          ctx.arc(PIVOT_X, PIVOT_Y, arcR, startAngle, startAngle + Math.abs(angleRad));
        }
        ctx.stroke();
        ctx.fillStyle = '#FF9800';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = angleDeg > 0 ? 'right' : 'left';
        const labelX = PIVOT_X + (angleDeg > 0 ? -45 : 45);
        ctx.fillText(`${formatNum(Math.abs(angleDeg), 1)}°`, labelX, PIVOT_Y + 50);
      }

      // Trail (ghost positions)
      if (time > 0) {
        ctx.globalAlpha = 0.1;
        const trailSteps = 20;
        for (let i = 0; i < trailSteps; i++) {
          const tBack = time - (i * period) / trailSteps * 0.3;
          if (tBack < 0) continue;
          const a = toRad(initAngle) * Math.cos(omega * tBack);
          const tx = PIVOT_X + length * PX_PER_M * Math.sin(a);
          const ty = PIVOT_Y + length * PX_PER_M * Math.cos(a);
          ctx.fillStyle = accentColor;
          ctx.beginPath();
          ctx.arc(tx, ty, 6, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      // Info
      ctx.fillStyle = '#333';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`T = 2\u03C0\u221A(L/g) = ${formatNum(period)} s`, 15, CANVAS_H - 50);
      ctx.fillText(`L = ${length.toFixed(1)} m`, 15, CANVAS_H - 30);
      ctx.font = '12px sans-serif';
      ctx.fillStyle = '#666';
      ctx.fillText('Period depends on length, NOT mass or amplitude (small angles)', 15, CANVAS_H - 10);

      // Length label on string
      ctx.save();
      ctx.translate((PIVOT_X + bobX) / 2 - 15, (PIVOT_Y + bobY) / 2);
      ctx.rotate(angleRad);
      ctx.fillStyle = '#888';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`L = ${length.toFixed(1)} m`, 0, -8);
      ctx.restore();
    },
    [length, initAngle, omega, period, accentColor],
  );

  useEffect(() => {
    setCurrentAngle(initAngle);
    drawScene(initAngle, 0);
  }, [length, initAngle, drawScene]);

  const startSim = useCallback(() => {
    setRunning(true);
    setSimTime(0);
    let startTime: number | null = null;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;

      const angle = initAngle * Math.cos(omega * elapsed);
      setCurrentAngle(angle);
      setSimTime(elapsed);
      drawScene(angle, elapsed);

      if (elapsed < Math.max(period * 5, 10)) {
        animRef.current = requestAnimationFrame(step);
      } else {
        setRunning(false);
      }
    };

    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [initAngle, omega, period, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setCurrentAngle(initAngle);
    setSimTime(0);
    drawScene(initAngle, 0);
  }, [initAngle, drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper>
        <StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} />
      </CanvasWrapper>

      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Length: {length.toFixed(1)} m</ControlLabel>
          <StyledSlider type="range" min={0.1} max={3} step={0.1} value={length} onChange={(e) => { setLength(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Initial Angle: {initAngle}&deg;</ControlLabel>
          <StyledSlider type="range" min={5} max={45} value={initAngle} onChange={(e) => { setInitAngle(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Release</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>

      <InfoPanel>
        <InfoItem><InfoLabel>Period (T = 2&pi;&radic;(L/g))</InfoLabel><InfoValue>{formatNum(period)} s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Frequency</InfoLabel><InfoValue>{formatNum(1 / period)} Hz</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Current Angle</InfoLabel><InfoValue>{formatNum(currentAngle)}&deg;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Length</InfoLabel><InfoValue>{length.toFixed(1)} m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Time</InfoLabel><InfoValue>{formatNum(simTime)} s</InfoValue></InfoItem>
      </InfoPanel>

      <Instructions>
        Adjust the pendulum length and initial angle, then press <strong>Release</strong>. Notice that the period depends <em>only</em> on the length, not the mass or initial angle (for small angles). A longer pendulum swings more slowly. T = 2&pi;&radic;(L/g).
      </Instructions>
    </>
  );
};

/* ─── 9. Motion (Position & Velocity) ─────────────────────────────────────── */

const MotionSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [x0, setX0] = useState(0);
  const [v0, setV0] = useState(3);
  const [acc, setAcc] = useState(0);
  const [running, setRunning] = useState(false);
  const [simTime, setSimTime] = useState(0);
  const [curX, setCurX] = useState(0);
  const [curV, setCurV] = useState(3);

  const ROAD_Y = CANVAS_H / 2 + 30;
  const SCALE = 20; // px per meter
  const ORIGIN_X = CANVAS_W / 2;

  const drawScene = useCallback(
    (posM: number, velM: number, t: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Road
      ctx.fillStyle = '#e0e0e0';
      ctx.fillRect(0, ROAD_Y - 2, CANVAS_W, 30);
      ctx.strokeStyle = '#FF9800';
      ctx.lineWidth = 2;
      ctx.setLineDash([12, 8]);
      ctx.beginPath();
      ctx.moveTo(0, ROAD_Y + 13);
      ctx.lineTo(CANVAS_W, ROAD_Y + 13);
      ctx.stroke();
      ctx.setLineDash([]);

      // Number line ticks
      ctx.fillStyle = '#999';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      for (let m = -15; m <= 15; m++) {
        const sx = ORIGIN_X + m * SCALE;
        if (sx < 5 || sx > CANVAS_W - 5) continue;
        const tickH = m % 5 === 0 ? 8 : 4;
        ctx.strokeStyle = '#bbb';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sx, ROAD_Y - 4);
        ctx.lineTo(sx, ROAD_Y - 4 - tickH);
        ctx.stroke();
        if (m % 5 === 0) ctx.fillText(`${m} m`, sx, ROAD_Y - 16);
      }

      // Origin marker
      ctx.strokeStyle = '#F44336';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(ORIGIN_X, ROAD_Y - 20);
      ctx.lineTo(ORIGIN_X, ROAD_Y + 30);
      ctx.stroke();
      ctx.fillStyle = '#F44336';
      ctx.font = 'bold 10px sans-serif';
      ctx.fillText('origin', ORIGIN_X, ROAD_Y + 42);

      // Car
      const carScreenX = ORIGIN_X + posM * SCALE;
      const carW = 40;
      const carH = 20;
      const carY = ROAD_Y - carH - 2;
      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.roundRect(carScreenX - carW / 2, carY, carW, carH, 4);
      ctx.fill();
      // Cabin
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.beginPath();
      ctx.roundRect(carScreenX - 8, carY - 10, 20, 12, [4, 4, 0, 0]);
      ctx.fill();
      // Wheels
      ctx.fillStyle = '#333';
      ctx.beginPath();
      ctx.arc(carScreenX - 12, ROAD_Y - 1, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(carScreenX + 12, ROAD_Y - 1, 5, 0, Math.PI * 2);
      ctx.fill();

      // Velocity arrow
      if (Math.abs(velM) > 0.1) {
        const arrLen = velM * 12;
        const arrY = carY - 18;
        ctx.strokeStyle = '#4CAF50';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(carScreenX, arrY);
        ctx.lineTo(carScreenX + arrLen, arrY);
        ctx.stroke();
        const dir = velM > 0 ? 1 : -1;
        ctx.fillStyle = '#4CAF50';
        ctx.beginPath();
        ctx.moveTo(carScreenX + arrLen + 6 * dir, arrY);
        ctx.lineTo(carScreenX + arrLen - 4 * dir, arrY - 5);
        ctx.lineTo(carScreenX + arrLen - 4 * dir, arrY + 5);
        ctx.closePath();
        ctx.fill();
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`v = ${formatNum(velM, 1)} m/s`, carScreenX + arrLen / 2, arrY - 10);
      }

      // Info banner
      ctx.fillStyle = '#333';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`x(t) = ${formatNum(x0, 1)} + ${formatNum(v0, 1)}t + ½(${formatNum(acc, 1)})t²`, CANVAS_W / 2, 25);
      ctx.font = '13px sans-serif';
      ctx.fillText(`Position: ${formatNum(posM, 2)} m   |   Velocity: ${formatNum(velM, 2)} m/s   |   t = ${formatNum(t, 1)} s`, CANVAS_W / 2, 48);
    },
    [x0, v0, acc, accentColor],
  );

  useEffect(() => {
    drawScene(x0, v0, 0);
  }, [drawScene, x0, v0]);

  const startSim = useCallback(() => {
    setRunning(true);
    setSimTime(0);
    let startTime: number | null = null;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      const simT = elapsed * 0.8;
      const x = x0 + v0 * simT + 0.5 * acc * simT * simT;
      const v = v0 + acc * simT;
      setCurX(x);
      setCurV(v);
      setSimTime(simT);
      drawScene(x, v, simT);
      if (simT < 8 && Math.abs(x) < 16) {
        animRef.current = requestAnimationFrame(step);
      } else {
        setRunning(false);
      }
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [x0, v0, acc, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setSimTime(0);
    setCurX(x0);
    setCurV(v0);
    drawScene(x0, v0, 0);
  }, [x0, v0, drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Initial Position (x₀): {x0} m</ControlLabel>
          <StyledSlider type="range" min={-10} max={10} step={0.5} value={x0} onChange={(e) => { setX0(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Initial Velocity (v₀): {v0} m/s</ControlLabel>
          <StyledSlider type="range" min={-5} max={5} step={0.5} value={v0} onChange={(e) => { setV0(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Acceleration (a): {acc} m/s&sup2;</ControlLabel>
          <StyledSlider type="range" min={-3} max={3} step={0.1} value={acc} onChange={(e) => { setAcc(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Start</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>Position (x)</InfoLabel><InfoValue>{formatNum(curX)} m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Velocity (v)</InfoLabel><InfoValue>{formatNum(curV)} m/s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Acceleration (a)</InfoLabel><InfoValue>{formatNum(acc)} m/s&sup2;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Time</InfoLabel><InfoValue>{formatNum(simTime)} s</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        Set the initial position, velocity, and acceleration of the car. Press <strong>Start</strong> to watch it move along the number line. Displacement is the change from the starting position. Positive velocity means rightward motion.
      </Instructions>
    </>
  );
};

/* ─── 10. Motion Graphs ───────────────────────────────────────────────────── */

const MotionGraphsSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [v0, setV0] = useState(2);
  const [acc, setAcc] = useState(1);
  const [running, setRunning] = useState(false);
  const [simTime, setSimTime] = useState(0);

  const drawScene = useCallback(
    (t: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      const graphW = 160;
      const graphH = 100;
      const graphs = [
        { label: 'x(t) — Position', color: '#2196F3', fn: (tt: number) => v0 * tt + 0.5 * acc * tt * tt, yMax: 40, x: 20, y: 20 },
        { label: 'v(t) — Velocity', color: '#4CAF50', fn: (tt: number) => v0 + acc * tt, yMax: 15, x: 215, y: 20 },
        { label: 'a(t) — Acceleration', color: '#F44336', fn: () => acc, yMax: 5, x: 410, y: 20 },
      ];

      const tMax = 6;
      for (const g of graphs) {
        const gx = g.x;
        const gy = g.y;

        // Background
        ctx.fillStyle = '#fff';
        ctx.fillRect(gx, gy, graphW, graphH);
        ctx.strokeStyle = '#ddd';
        ctx.lineWidth = 1;
        ctx.strokeRect(gx, gy, graphW, graphH);

        // Label
        ctx.fillStyle = '#333';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(g.label, gx + graphW / 2, gy - 5);

        // Axes
        ctx.strokeStyle = '#999';
        ctx.lineWidth = 1;
        const zeroY = gy + graphH / 2;
        ctx.beginPath();
        ctx.moveTo(gx, zeroY);
        ctx.lineTo(gx + graphW, zeroY);
        ctx.stroke();

        // Axis labels
        ctx.fillStyle = '#aaa';
        ctx.font = '9px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(`${g.yMax}`, gx - 3, gy + 8);
        ctx.fillText(`-${g.yMax}`, gx - 3, gy + graphH);
        ctx.textAlign = 'center';
        ctx.fillText('t', gx + graphW + 8, zeroY + 4);

        // Plot
        ctx.strokeStyle = g.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        let started = false;
        for (let px = 0; px < graphW; px++) {
          const tt = (px / graphW) * tMax;
          if (tt > t) break;
          const val = g.fn(tt);
          const py = zeroY - (val / g.yMax) * (graphH / 2);
          const clampedPy = Math.max(gy, Math.min(gy + graphH, py));
          if (!started) { ctx.moveTo(gx + px, clampedPy); started = true; }
          else ctx.lineTo(gx + px, clampedPy);
        }
        ctx.stroke();

        // Dot at current time
        if (t > 0) {
          const px = (t / tMax) * graphW;
          if (px <= graphW) {
            const val = g.fn(t);
            const py = zeroY - (val / g.yMax) * (graphH / 2);
            const clampedPy = Math.max(gy, Math.min(gy + graphH, py));
            ctx.fillStyle = g.color;
            ctx.beginPath();
            ctx.arc(gx + px, clampedPy, 4, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      // Bottom: animated object on a track
      const trackY = 180;
      const trackW = CANVAS_W - 60;
      const trackX = 30;

      // Track
      ctx.fillStyle = '#e0e0e0';
      ctx.fillRect(trackX, trackY, trackW, 6);

      // Object position
      const posM = v0 * t + 0.5 * acc * t * t;
      const maxPos = v0 * tMax + 0.5 * acc * tMax * tMax;
      const objX = trackX + (posM / Math.max(maxPos, 1)) * trackW;
      const clampedObjX = Math.max(trackX, Math.min(trackX + trackW, objX));

      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.arc(clampedObjX, trackY + 3, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Values
      const curX = v0 * t + 0.5 * acc * t * t;
      const curV = v0 + acc * t;
      ctx.fillStyle = '#333';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`x = ${formatNum(curX, 1)} m    v = ${formatNum(curV, 1)} m/s    a = ${formatNum(acc, 1)} m/s²    t = ${formatNum(t, 1)} s`, CANVAS_W / 2, trackY + 35);

      // Key insight
      ctx.font = 'bold 11px sans-serif';
      ctx.fillStyle = '#666';
      ctx.fillText('Slope of x-t = velocity  |  Slope of v-t = acceleration  |  Area under v-t = displacement', CANVAS_W / 2, trackY + 55);
    },
    [v0, acc, accentColor],
  );

  useEffect(() => { drawScene(0); }, [drawScene]);

  const startSim = useCallback(() => {
    setRunning(true);
    setSimTime(0);
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      const simT = elapsed * 0.7;
      setSimTime(simT);
      drawScene(simT);
      if (simT < 6) animRef.current = requestAnimationFrame(step);
      else setRunning(false);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setSimTime(0);
    drawScene(0);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Initial Velocity: {v0} m/s</ControlLabel>
          <StyledSlider type="range" min={-3} max={5} step={0.5} value={v0} onChange={(e) => { setV0(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Acceleration: {acc} m/s&sup2;</ControlLabel>
          <StyledSlider type="range" min={-3} max={3} step={0.25} value={acc} onChange={(e) => { setAcc(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Start</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>Position</InfoLabel><InfoValue>{formatNum(v0 * simTime + 0.5 * acc * simTime * simTime)} m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Velocity</InfoLabel><InfoValue>{formatNum(v0 + acc * simTime)} m/s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Acceleration</InfoLabel><InfoValue>{formatNum(acc)} m/s&sup2;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Time</InfoLabel><InfoValue>{formatNum(simTime)} s</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        Watch the x-t, v-t, and a-t graphs build in real time. The slope of the position graph gives velocity; the slope of the velocity graph gives acceleration. With constant acceleration, x-t is parabolic, v-t is linear, and a-t is flat.
      </Instructions>
    </>
  );
};

/* ─── 11. Tension & Atwood Machine ────────────────────────────────────────── */

const TensionAtwoodSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [m1, setM1] = useState(5);
  const [m2, setM2] = useState(3);
  const [running, setRunning] = useState(false);
  const [simTime, setSimTime] = useState(0);
  const [, setOffset] = useState(0);

  const accCalc = ((m1 - m2) * G) / (m1 + m2);
  const tension = (2 * m1 * m2 * G) / (m1 + m2);

  const PULLEY_X = CANVAS_W / 2;
  const PULLEY_Y = 60;
  const PULLEY_R = 20;
  const LEFT_X = PULLEY_X - 80;
  const RIGHT_X = PULLEY_X + 80;

  const drawScene = useCallback(
    (off: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Ceiling
      ctx.fillStyle = '#78909c';
      ctx.fillRect(PULLEY_X - 60, 0, 120, 25);
      ctx.strokeStyle = '#546e7a';
      ctx.lineWidth = 1;
      for (let i = -60; i < 60; i += 10) {
        ctx.beginPath();
        ctx.moveTo(PULLEY_X + i, 0);
        ctx.lineTo(PULLEY_X + i + 8, 25);
        ctx.stroke();
      }

      // Pulley support
      ctx.strokeStyle = '#555';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(PULLEY_X, 25);
      ctx.lineTo(PULLEY_X, PULLEY_Y);
      ctx.stroke();

      // Pulley
      ctx.fillStyle = '#78909c';
      ctx.beginPath();
      ctx.arc(PULLEY_X, PULLEY_Y, PULLEY_R, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#546e7a';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#546e7a';
      ctx.beginPath();
      ctx.arc(PULLEY_X, PULLEY_Y, 4, 0, Math.PI * 2);
      ctx.fill();

      // Ropes and masses
      const baseY = 160;
      const m1Y = baseY + off * 2;
      const m2Y = baseY - off * 2;
      const boxW = 50;
      const boxH = 40;

      // Left rope (m1, heavier, goes down)
      ctx.strokeStyle = '#8D6E63';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(PULLEY_X - PULLEY_R, PULLEY_Y);
      ctx.lineTo(LEFT_X, PULLEY_Y);
      ctx.lineTo(LEFT_X, m1Y);
      ctx.stroke();

      // Right rope (m2)
      ctx.beginPath();
      ctx.moveTo(PULLEY_X + PULLEY_R, PULLEY_Y);
      ctx.lineTo(RIGHT_X, PULLEY_Y);
      ctx.lineTo(RIGHT_X, m2Y);
      ctx.stroke();

      // Mass 1 (left)
      ctx.fillStyle = '#2196F3';
      ctx.fillRect(LEFT_X - boxW / 2, m1Y, boxW, boxH);
      ctx.strokeStyle = '#1565C0';
      ctx.lineWidth = 2;
      ctx.strokeRect(LEFT_X - boxW / 2, m1Y, boxW, boxH);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${m1} kg`, LEFT_X, m1Y + boxH / 2 + 5);

      // Mass 2 (right)
      ctx.fillStyle = '#F44336';
      ctx.fillRect(RIGHT_X - boxW / 2, m2Y, boxW, boxH);
      ctx.strokeStyle = '#C62828';
      ctx.lineWidth = 2;
      ctx.strokeRect(RIGHT_X - boxW / 2, m2Y, boxW, boxH);
      ctx.fillStyle = '#fff';
      ctx.fillText(`${m2} kg`, RIGHT_X, m2Y + boxH / 2 + 5);

      // Tension arrows
      const tLen = Math.min(tension * 1.5, 60);
      // T on m1 (up)
      ctx.strokeStyle = '#FF9800';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(LEFT_X, m1Y);
      ctx.lineTo(LEFT_X, m1Y - tLen);
      ctx.stroke();
      ctx.fillStyle = '#FF9800';
      ctx.beginPath();
      ctx.moveTo(LEFT_X, m1Y - tLen - 6);
      ctx.lineTo(LEFT_X - 5, m1Y - tLen + 4);
      ctx.lineTo(LEFT_X + 5, m1Y - tLen + 4);
      ctx.closePath();
      ctx.fill();
      ctx.font = 'bold 11px sans-serif';
      ctx.fillText(`T`, LEFT_X - 15, m1Y - tLen / 2);

      // T on m2 (up)
      ctx.beginPath();
      ctx.moveTo(RIGHT_X, m2Y);
      ctx.lineTo(RIGHT_X, m2Y - tLen);
      ctx.stroke();
      ctx.fillStyle = '#FF9800';
      ctx.beginPath();
      ctx.moveTo(RIGHT_X, m2Y - tLen - 6);
      ctx.lineTo(RIGHT_X - 5, m2Y - tLen + 4);
      ctx.lineTo(RIGHT_X + 5, m2Y - tLen + 4);
      ctx.closePath();
      ctx.fill();
      ctx.fillText(`T`, RIGHT_X + 15, m2Y - tLen / 2);

      // Weight arrows (down)
      const w1Len = Math.min(m1 * G * 0.12, 50);
      const w2Len = Math.min(m2 * G * 0.12, 50);
      ctx.strokeStyle = '#9C27B0';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(LEFT_X, m1Y + boxH);
      ctx.lineTo(LEFT_X, m1Y + boxH + w1Len);
      ctx.stroke();
      ctx.fillStyle = '#9C27B0';
      ctx.beginPath();
      ctx.moveTo(LEFT_X, m1Y + boxH + w1Len + 6);
      ctx.lineTo(LEFT_X - 5, m1Y + boxH + w1Len - 4);
      ctx.lineTo(LEFT_X + 5, m1Y + boxH + w1Len - 4);
      ctx.closePath();
      ctx.fill();
      ctx.font = '10px sans-serif';
      ctx.fillText(`${formatNum(m1 * G, 0)} N`, LEFT_X, m1Y + boxH + w1Len + 18);

      ctx.strokeStyle = '#9C27B0';
      ctx.beginPath();
      ctx.moveTo(RIGHT_X, m2Y + boxH);
      ctx.lineTo(RIGHT_X, m2Y + boxH + w2Len);
      ctx.stroke();
      ctx.fillStyle = '#9C27B0';
      ctx.beginPath();
      ctx.moveTo(RIGHT_X, m2Y + boxH + w2Len + 6);
      ctx.lineTo(RIGHT_X - 5, m2Y + boxH + w2Len - 4);
      ctx.lineTo(RIGHT_X + 5, m2Y + boxH + w2Len - 4);
      ctx.closePath();
      ctx.fill();
      ctx.fillText(`${formatNum(m2 * G, 0)} N`, RIGHT_X, m2Y + boxH + w2Len + 18);

      // Equations
      ctx.fillStyle = '#333';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`a = (m₁ − m₂)g / (m₁ + m₂) = ${formatNum(accCalc, 2)} m/s²`, CANVAS_W / 2, CANVAS_H - 30);
      ctx.fillText(`T = 2m₁m₂g / (m₁ + m₂) = ${formatNum(tension, 1)} N`, CANVAS_W / 2, CANVAS_H - 10);
    },
    [m1, m2, accCalc, tension],
  );

  useEffect(() => { setOffset(0); drawScene(0); }, [drawScene]);

  const startSim = useCallback(() => {
    setRunning(true);
    setSimTime(0);
    setOffset(0);
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      const simT = elapsed * 0.8;
      const off = 0.5 * accCalc * simT * simT * 10;
      setOffset(off);
      setSimTime(simT);
      drawScene(off);
      if (Math.abs(off) < 100 && simT < 5) animRef.current = requestAnimationFrame(step);
      else setRunning(false);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [accCalc, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setSimTime(0);
    setOffset(0);
    drawScene(0);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Mass 1 (left, blue): {m1} kg</ControlLabel>
          <StyledSlider type="range" min={1} max={10} value={m1} onChange={(e) => { setM1(+e.target.value); if (!running) resetSim(); }} $accent="#2196F3" />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Mass 2 (right, red): {m2} kg</ControlLabel>
          <StyledSlider type="range" min={1} max={10} value={m2} onChange={(e) => { setM2(+e.target.value); if (!running) resetSim(); }} $accent="#F44336" />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Release</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>Tension (T)</InfoLabel><InfoValue>{formatNum(tension)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Acceleration (a)</InfoLabel><InfoValue>{formatNum(accCalc)} m/s&sup2;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Weight 1 (m₁g)</InfoLabel><InfoValue>{formatNum(m1 * G)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Weight 2 (m₂g)</InfoLabel><InfoValue>{formatNum(m2 * G)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Time</InfoLabel><InfoValue>{formatNum(simTime)} s</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        An Atwood machine has two masses connected by a rope over a pulley. The heavier mass accelerates downward. The tension in the rope is the same throughout. When masses are equal, acceleration is zero and T = mg.
      </Instructions>
    </>
  );
};

/* ─── 12. Inclined Plane ──────────────────────────────────────────────────── */

const InclinedPlaneSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [angle, setAngle] = useState(30);
  const [mass, setMass] = useState(5);
  const [mu, setMu] = useState(0.2);
  const [running, setRunning] = useState(false);
  const [, setBlockPos] = useState(0);
  const [, setSimTime] = useState(0);

  const angleRad = toRad(angle);
  const mgSinTheta = mass * G * Math.sin(angleRad);
  const mgCosTheta = mass * G * Math.cos(angleRad);
  const frictionF = mu * mgCosTheta;
  const netForce = Math.max(mgSinTheta - frictionF, 0);
  const accNet = netForce / mass;

  const drawScene = useCallback(
    (pos: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Ramp geometry
      const rampBaseX = 80;
      const rampBaseY = CANVAS_H - 60;
      const rampLen = 400;
      const rampTopX = rampBaseX + rampLen * Math.cos(angleRad);
      const rampTopY = rampBaseY - rampLen * Math.sin(angleRad);

      // Ground
      ctx.fillStyle = '#e0e0e0';
      ctx.fillRect(0, rampBaseY, CANVAS_W, CANVAS_H - rampBaseY);
      ctx.strokeStyle = '#90a4ae';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, rampBaseY);
      ctx.lineTo(CANVAS_W, rampBaseY);
      ctx.stroke();

      // Ramp
      ctx.fillStyle = '#d7ccc8';
      ctx.beginPath();
      ctx.moveTo(rampBaseX, rampBaseY);
      ctx.lineTo(rampTopX, rampTopY);
      ctx.lineTo(rampTopX, rampBaseY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#8D6E63';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Angle arc
      ctx.strokeStyle = '#FF9800';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(rampBaseX, rampBaseY, 40, -Math.PI / 2 + (Math.PI / 2 - angleRad), -Math.PI / 2 + Math.PI / 2);
      ctx.stroke();
      ctx.fillStyle = '#FF9800';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`θ = ${angle}°`, rampBaseX + 45, rampBaseY - 10);

      // Block on ramp
      const blockDist = pos;
      const blockCenterX = rampTopX - blockDist * Math.cos(angleRad);
      const blockCenterY = rampTopY + blockDist * Math.sin(angleRad);
      const bW = 40;
      const bH = 30;

      ctx.save();
      ctx.translate(blockCenterX, blockCenterY);
      ctx.rotate(-angleRad);

      // Block
      ctx.fillStyle = accentColor;
      ctx.fillRect(-bW / 2, -bH, bW, bH);
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 2;
      ctx.strokeRect(-bW / 2, -bH, bW, bH);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${mass}kg`, 0, -bH / 2 + 4);

      ctx.restore();

      // Force arrows from block center (in world coords)
      const arrowScale = 1.2;
      const cx = blockCenterX;
      const cy = blockCenterY - bH / 2;

      // mg (straight down)
      const mgLen = Math.min(mass * G * arrowScale, 80);
      ctx.strokeStyle = '#9C27B0';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx, cy + mgLen);
      ctx.stroke();
      ctx.fillStyle = '#9C27B0';
      ctx.beginPath();
      ctx.moveTo(cx, cy + mgLen + 6);
      ctx.lineTo(cx - 5, cy + mgLen - 4);
      ctx.lineTo(cx + 5, cy + mgLen - 4);
      ctx.closePath();
      ctx.fill();
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('mg', cx + 8, cy + mgLen);

      // Normal force (perpendicular to ramp, away from surface)
      const nLen = Math.min(mgCosTheta * arrowScale, 60);
      const nx = -Math.sin(angleRad) * nLen;
      const ny = -Math.cos(angleRad) * nLen;
      ctx.strokeStyle = '#2196F3';
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + nx, cy + ny);
      ctx.stroke();
      ctx.fillStyle = '#2196F3';
      ctx.font = 'bold 10px sans-serif';
      ctx.fillText(`N = ${formatNum(mgCosTheta, 0)}N`, cx + nx + 5, cy + ny);

      // mg sin θ (along ramp, downhill)
      const sinLen = Math.min(mgSinTheta * arrowScale, 60);
      const sx = Math.cos(angleRad) * sinLen;
      const sy = -Math.sin(angleRad) * sinLen;
      ctx.strokeStyle = '#F44336';
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx - sx, cy - sy);
      ctx.stroke();
      ctx.fillStyle = '#F44336';
      ctx.fillText(`mg sinθ = ${formatNum(mgSinTheta, 0)}N`, cx - sx - 10, cy - sy + 15);

      // Friction (along ramp, uphill)
      if (frictionF > 0.5) {
        const fLen = Math.min(frictionF * arrowScale, 60);
        const fx = Math.cos(angleRad) * fLen;
        const fy = -Math.sin(angleRad) * fLen;
        ctx.strokeStyle = '#FF9800';
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + fx, cy + fy);
        ctx.stroke();
        ctx.fillStyle = '#FF9800';
        ctx.fillText(`f = ${formatNum(frictionF, 0)}N`, cx + fx + 5, cy + fy - 5);
      }

      // Info
      ctx.fillStyle = '#333';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      const willSlide = mgSinTheta > frictionF;
      ctx.fillText(
        willSlide ? `Net force = ${formatNum(netForce, 1)} N down the ramp → a = ${formatNum(accNet, 2)} m/s²` : 'Friction prevents sliding (mg sinθ ≤ μN)',
        CANVAS_W / 2, 25,
      );
    },
    [angle, mass, mu, accentColor, angleRad, mgSinTheta, mgCosTheta, frictionF, netForce, accNet],
  );

  useEffect(() => { setBlockPos(0); drawScene(0); }, [drawScene]);

  const startSim = useCallback(() => {
    if (accNet <= 0) return;
    setRunning(true);
    setBlockPos(0);
    setSimTime(0);
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      const simT = elapsed * 0.8;
      const pos = 0.5 * accNet * simT * simT * 8;
      setBlockPos(pos);
      setSimTime(simT);
      drawScene(pos);
      if (pos < 350 && simT < 5) animRef.current = requestAnimationFrame(step);
      else setRunning(false);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [accNet, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setBlockPos(0);
    setSimTime(0);
    drawScene(0);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Angle: {angle}&deg;</ControlLabel>
          <StyledSlider type="range" min={5} max={75} value={angle} onChange={(e) => { setAngle(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Mass: {mass} kg</ControlLabel>
          <StyledSlider type="range" min={1} max={15} value={mass} onChange={(e) => { setMass(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Friction (&mu;): {mu.toFixed(2)}</ControlLabel>
          <StyledSlider type="range" min={0} max={1} step={0.05} value={mu} onChange={(e) => { setMu(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running || accNet <= 0}>{accNet > 0 ? 'Release' : 'No Sliding'}</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>mg sin&theta;</InfoLabel><InfoValue>{formatNum(mgSinTheta)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>mg cos&theta;</InfoLabel><InfoValue>{formatNum(mgCosTheta)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Normal Force</InfoLabel><InfoValue>{formatNum(mgCosTheta)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Friction</InfoLabel><InfoValue>{formatNum(frictionF)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Net Force</InfoLabel><InfoValue>{formatNum(netForce)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Acceleration</InfoLabel><InfoValue>{formatNum(accNet)} m/s&sup2;</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        A block on an inclined plane has its weight decomposed into mg sin&theta; (along the ramp) and mg cos&theta; (into the ramp). The normal force equals mg cos&theta;. Friction opposes motion. The block slides when mg sin&theta; exceeds friction (&mu;N).
      </Instructions>
    </>
  );
};

/* ─── 13. Circular Motion ─────────────────────────────────────────────────── */

const CircularMotionSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [radius, setRadius] = useState(2);
  const [speed, setSpeed] = useState(4);
  const [mass, setMass] = useState(2);
  const [running, setRunning] = useState(false);
  const [, setTheta] = useState(0);

  const ac = (speed * speed) / radius;
  const Fc = mass * ac;
  const period = (2 * Math.PI * radius) / speed;

  const CX = CANVAS_W / 2 - 40;
  const CY = CANVAS_H / 2;
  const PX_PER_M = 55;

  const drawScene = useCallback(
    (angle: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      const rPx = radius * PX_PER_M;

      // Circular path (dashed)
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(CX, CY, rPx, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // Center point
      ctx.fillStyle = '#999';
      ctx.beginPath();
      ctx.arc(CX, CY, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#999';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('center', CX, CY + 16);

      // Ball position
      const bx = CX + rPx * Math.cos(angle);
      const by = CY + rPx * Math.sin(angle);

      // String from center to ball
      ctx.strokeStyle = '#8D6E63';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(CX, CY);
      ctx.lineTo(bx, by);
      ctx.stroke();

      // Radius label
      ctx.fillStyle = '#8D6E63';
      ctx.font = '11px sans-serif';
      ctx.save();
      ctx.translate((CX + bx) / 2, (CY + by) / 2);
      ctx.fillText(`r = ${radius} m`, 0, -8);
      ctx.restore();

      // Ball
      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.arc(bx, by, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Centripetal acceleration arrow (toward center)
      const acLen = Math.min(ac * 5, 70);
      const toCenterX = (CX - bx);
      const toCenterY = (CY - by);
      const dist = Math.sqrt(toCenterX * toCenterX + toCenterY * toCenterY);
      const ux = toCenterX / dist;
      const uy = toCenterY / dist;

      ctx.strokeStyle = '#F44336';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + ux * acLen, by + uy * acLen);
      ctx.stroke();
      ctx.fillStyle = '#F44336';
      ctx.beginPath();
      ctx.moveTo(bx + ux * (acLen + 6), by + uy * (acLen + 6));
      ctx.lineTo(bx + ux * acLen + uy * 5, by + uy * acLen - ux * 5);
      ctx.lineTo(bx + ux * acLen - uy * 5, by + uy * acLen + ux * 5);
      ctx.closePath();
      ctx.fill();
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`ac = ${formatNum(ac, 1)} m/s²`, bx + ux * acLen / 2 + uy * 18, by + uy * acLen / 2 - ux * 18);

      // Velocity arrow (tangent)
      const vLen = Math.min(speed * 10, 60);
      const vx = -uy * vLen;
      const vy = ux * vLen;
      ctx.strokeStyle = '#4CAF50';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + vx, by + vy);
      ctx.stroke();
      ctx.fillStyle = '#4CAF50';
      ctx.beginPath();
      ctx.moveTo(bx + vx + (-uy) * 6, by + vy + ux * 6);
      ctx.lineTo(bx + vx + uy * 5 + (-uy) * (-1), by + vy - ux * 5 + ux * (-1));
      ctx.lineTo(bx + vx - uy * 5 + (-uy) * (-1), by + vy + ux * 5 + ux * (-1));
      ctx.closePath();
      ctx.fill();
      ctx.fillText(`v = ${speed} m/s`, bx + vx / 2 - uy * 15, by + vy / 2 + ux * 15);

      // Info box (right side)
      const infoX = CANVAS_W - 170;
      ctx.fillStyle = '#333';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('Centripetal:', infoX, 30);
      ctx.font = '12px sans-serif';
      ctx.fillText(`ac = v²/r = ${formatNum(ac, 2)} m/s²`, infoX, 50);
      ctx.fillText(`Fc = mac = ${formatNum(Fc, 1)} N`, infoX, 70);
      ctx.fillText(`T = 2πr/v = ${formatNum(period, 2)} s`, infoX, 90);
      ctx.fillStyle = '#F44336';
      ctx.fillText('← acceleration (inward)', infoX, 115);
      ctx.fillStyle = '#4CAF50';
      ctx.fillText('← velocity (tangent)', infoX, 135);
    },
    [radius, speed, mass, ac, Fc, period, accentColor],
  );

  useEffect(() => { drawScene(0); }, [drawScene]);

  const startSim = useCallback(() => {
    setRunning(true);
    setTheta(0);
    let lastTimestamp: number | null = null;
    let angle = 0;
    const angularSpeed = speed / radius;

    const step = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const dt = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;
      angle += angularSpeed * dt;
      setTheta(angle);
      drawScene(angle);
      if (angle < Math.PI * 6) animRef.current = requestAnimationFrame(step);
      else setRunning(false);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [speed, radius, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setTheta(0);
    drawScene(0);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Radius: {radius} m</ControlLabel>
          <StyledSlider type="range" min={0.5} max={3} step={0.25} value={radius} onChange={(e) => { setRadius(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Speed: {speed} m/s</ControlLabel>
          <StyledSlider type="range" min={1} max={8} step={0.5} value={speed} onChange={(e) => { setSpeed(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Mass: {mass} kg</ControlLabel>
          <StyledSlider type="range" min={0.5} max={5} step={0.5} value={mass} onChange={(e) => { setMass(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Spin</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>Centripetal Accel (v²/r)</InfoLabel><InfoValue>{formatNum(ac)} m/s&sup2;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Centripetal Force (mac)</InfoLabel><InfoValue>{formatNum(Fc)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Period (2&pi;r/v)</InfoLabel><InfoValue>{formatNum(period)} s</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        A ball moves in a circle at constant speed. The velocity is always tangent to the circle (green arrow). The acceleration always points toward the center (red arrow). Greater speed or smaller radius means greater centripetal acceleration (a = v²/r).
      </Instructions>
    </>
  );
};

/* ─── 14. Gravity & Orbits ────────────────────────────────────────────────── */

const GravityOrbitSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [starMass, setStarMass] = useState(100);
  const [planetDist, setPlanetDist] = useState(120);
  const [running, setRunning] = useState(false);
  const [, setTheta] = useState(0);

  const CX = CANVAS_W / 2;
  const CY = CANVAS_H / 2;

  // Scaled physics for visualization
  const scaledG = 500;
  const orbitalSpeed = Math.sqrt(scaledG * starMass / planetDist);
  const orbitalPeriod = (2 * Math.PI * planetDist) / orbitalSpeed;
  const gravForce = scaledG * starMass / (planetDist * planetDist);

  const drawScene = useCallback(
    (angle: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Space background
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Stars
      for (let i = 0; i < 50; i++) {
        const sx = ((i * 137 + 43) % CANVAS_W);
        const sy = ((i * 97 + 19) % CANVAS_H);
        ctx.fillStyle = `rgba(255,255,255,${0.3 + (i % 5) * 0.15})`;
        ctx.beginPath();
        ctx.arc(sx, sy, 0.5 + (i % 3) * 0.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Orbit path
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = 'rgba(255,255,255,0.2)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(CX, CY, planetDist, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // Star (sun)
      const starR = 15 + starMass * 0.08;
      const grad = ctx.createRadialGradient(CX, CY, 0, CX, CY, starR);
      grad.addColorStop(0, '#FFF9C4');
      grad.addColorStop(0.5, '#FFD54F');
      grad.addColorStop(1, '#FF8F00');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(CX, CY, starR, 0, Math.PI * 2);
      ctx.fill();

      // Glow
      ctx.shadowColor = '#FFD54F';
      ctx.shadowBlur = 20;
      ctx.beginPath();
      ctx.arc(CX, CY, starR, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Planet
      const px = CX + planetDist * Math.cos(angle);
      const py = CY + planetDist * Math.sin(angle);
      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.arc(px, py, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Gravity arrow (planet toward star)
      const fLen = Math.min(gravForce * 15, 50);
      const ux = (CX - px) / planetDist;
      const uy = (CY - py) / planetDist;
      ctx.strokeStyle = '#F44336';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + ux * fLen, py + uy * fLen);
      ctx.stroke();
      ctx.fillStyle = '#F44336';
      ctx.beginPath();
      ctx.moveTo(px + ux * (fLen + 5), py + uy * (fLen + 5));
      ctx.lineTo(px + ux * fLen + uy * 4, py + uy * fLen - ux * 4);
      ctx.lineTo(px + ux * fLen - uy * 4, py + uy * fLen + ux * 4);
      ctx.closePath();
      ctx.fill();

      // Velocity arrow (tangent)
      const vLen = Math.min(orbitalSpeed * 3, 40);
      ctx.strokeStyle = '#4CAF50';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px - uy * vLen, py + ux * vLen);
      ctx.stroke();
      ctx.fillStyle = '#4CAF50';
      const tipX = px - uy * vLen;
      const tipY = py + ux * vLen;
      ctx.beginPath();
      ctx.moveTo(tipX + (-uy) * 5, tipY + ux * 5);
      ctx.lineTo(tipX + uy * 4 + (-uy) * (-1), tipY - ux * 4 + ux * (-1));
      ctx.lineTo(tipX - uy * 4 + (-uy) * (-1), tipY + ux * 4 + ux * (-1));
      ctx.closePath();
      ctx.fill();

      // Distance label
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(CX, CY);
      ctx.lineTo(px, py);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`r`, (CX + px) / 2 + 8, (CY + py) / 2 - 5);

      // Info text
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('F = GMm/r²', 15, 25);
      ctx.font = '11px sans-serif';
      ctx.fillStyle = '#F44336';
      ctx.fillText('Gravity (inward)', 15, 45);
      ctx.fillStyle = '#4CAF50';
      ctx.fillText('Velocity (tangent)', 15, 60);
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fillText(`T = ${formatNum(orbitalPeriod / 60, 1)} (arb. units)`, 15, 80);
    },
    [starMass, planetDist, accentColor, gravForce, orbitalSpeed, orbitalPeriod],
  );

  useEffect(() => { drawScene(0); }, [drawScene]);

  const startSim = useCallback(() => {
    setRunning(true);
    let lastTimestamp: number | null = null;
    let angle = 0;
    const angVel = orbitalSpeed / planetDist;

    const step = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const dt = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;
      angle += angVel * dt;
      setTheta(angle);
      drawScene(angle);
      if (angle < Math.PI * 8) animRef.current = requestAnimationFrame(step);
      else setRunning(false);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [orbitalSpeed, planetDist, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setTheta(0);
    drawScene(0);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Star Mass: {starMass} (arb. units)</ControlLabel>
          <StyledSlider type="range" min={20} max={200} value={starMass} onChange={(e) => { setStarMass(+e.target.value); if (!running) resetSim(); }} $accent="#FFD54F" />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Orbital Radius: {planetDist} px</ControlLabel>
          <StyledSlider type="range" min={60} max={180} value={planetDist} onChange={(e) => { setPlanetDist(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Orbit</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>Gravitational Force</InfoLabel><InfoValue>F &prop; M/r&sup2;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Orbital Speed</InfoLabel><InfoValue>&radic;(GM/r)</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Period</InfoLabel><InfoValue>{formatNum(orbitalPeriod / 60, 1)} units</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        A planet orbits a star held by gravity (F = GMm/r²). Gravity pulls inward (red), while the planet's velocity is tangential (green). A larger star mass or smaller orbit means faster orbital speed. In a stable orbit, gravity provides the centripetal force.
      </Instructions>
    </>
  );
};

/* ─── 15. Work ────────────────────────────────────────────────────────────── */

const WorkSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [force, setForce] = useState(30);
  const [forceAngle, setForceAngle] = useState(0);
  const [running, setRunning] = useState(false);
  const [, setBoxX] = useState(80);
  const [, setDistMoved] = useState(0);

  const forceAngleRad = toRad(forceAngle);
  const GROUND_Y = CANVAS_H - 80;
  const BOX_W = 50;
  const BOX_H = 40;
  const distM = 5; // fixed distance of 5m

  const workDone = force * distM * Math.cos(forceAngleRad);
  const horizontalComponent = force * Math.cos(forceAngleRad);

  const drawScene = useCallback(
    (bx: number, moved: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Ground
      ctx.fillStyle = '#eceff1';
      ctx.fillRect(0, GROUND_Y, CANVAS_W, CANVAS_H - GROUND_Y);
      ctx.strokeStyle = '#90a4ae';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y);
      ctx.lineTo(CANVAS_W, GROUND_Y);
      ctx.stroke();

      // Distance markers
      ctx.fillStyle = '#999';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      for (let m = 0; m <= 8; m++) {
        const mx = 80 + m * 50;
        ctx.strokeStyle = '#ddd';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(mx, GROUND_Y);
        ctx.lineTo(mx, GROUND_Y + 5);
        ctx.stroke();
        ctx.fillText(`${m}m`, mx, GROUND_Y + 16);
      }

      // Moved distance indicator
      if (moved > 0) {
        ctx.fillStyle = 'rgba(76,175,80,0.15)';
        ctx.fillRect(80, GROUND_Y - BOX_H - 10, moved * 50, BOX_H + 10);
        ctx.fillStyle = '#4CAF50';
        ctx.font = 'bold 11px sans-serif';
        ctx.fillText(`d = ${formatNum(moved, 1)} m`, 80 + moved * 25, GROUND_Y - BOX_H - 15);
      }

      // Box
      const bxC = Math.max(10, Math.min(bx, CANVAS_W - BOX_W - 10));
      const by = GROUND_Y - BOX_H;
      ctx.fillStyle = accentColor;
      ctx.fillRect(bxC, by, BOX_W, BOX_H);
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 2;
      ctx.strokeRect(bxC, by, BOX_W, BOX_H);

      // Force arrow at angle
      const arrLen = Math.min(force * 1.5, 100);
      const arrStartX = bxC + BOX_W + 5;
      const arrStartY = by + BOX_H / 2;
      const arrEndX = arrStartX + arrLen * Math.cos(forceAngleRad);
      const arrEndY = arrStartY - arrLen * Math.sin(forceAngleRad);

      // Force arrow
      ctx.strokeStyle = '#F44336';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(arrStartX, arrStartY);
      ctx.lineTo(arrEndX, arrEndY);
      ctx.stroke();
      ctx.fillStyle = '#F44336';
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`F = ${force} N`, arrEndX + 5, arrEndY - 5);

      // Angle arc
      if (forceAngle > 2) {
        ctx.strokeStyle = '#FF9800';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(arrStartX, arrStartY, 25, -forceAngleRad, 0);
        ctx.stroke();
        ctx.fillStyle = '#FF9800';
        ctx.font = '11px sans-serif';
        ctx.fillText(`θ = ${forceAngle}°`, arrStartX + 28, arrStartY - 5);
      }

      // Horizontal component (dashed)
      if (forceAngle > 5) {
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = '#2196F3';
        ctx.lineWidth = 2;
        const hLen = arrLen * Math.cos(forceAngleRad);
        ctx.beginPath();
        ctx.moveTo(arrStartX, arrStartY);
        ctx.lineTo(arrStartX + hLen, arrStartY);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#2196F3';
        ctx.font = '10px sans-serif';
        ctx.fillText(`F cosθ = ${formatNum(horizontalComponent, 0)} N`, arrStartX + hLen / 2, arrStartY + 15);
      }

      // Equation
      ctx.fillStyle = '#333';
      ctx.font = 'bold 15px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`W = F·d·cos θ = ${force} × ${formatNum(moved > 0 ? moved : distM, 1)} × cos ${forceAngle}° = ${formatNum(moved > 0 ? force * moved * Math.cos(forceAngleRad) : workDone, 1)} J`, CANVAS_W / 2, 30);
    },
    [force, forceAngle, forceAngleRad, horizontalComponent, accentColor, workDone, distM],
  );

  useEffect(() => { drawScene(80, 0); }, [drawScene]);

  const startSim = useCallback(() => {
    setRunning(true);
    setBoxX(80);
    setDistMoved(0);
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      const moved = Math.min(elapsed * 1.5, distM);
      const bx = 80 + moved * 50;
      setBoxX(bx);
      setDistMoved(moved);
      drawScene(bx, moved);
      if (moved < distM) animRef.current = requestAnimationFrame(step);
      else setRunning(false);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [drawScene, distM]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setBoxX(80);
    setDistMoved(0);
    drawScene(80, 0);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Force: {force} N</ControlLabel>
          <StyledSlider type="range" min={5} max={80} value={force} onChange={(e) => { setForce(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Force Angle: {forceAngle}&deg;</ControlLabel>
          <StyledSlider type="range" min={0} max={85} value={forceAngle} onChange={(e) => { setForceAngle(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Push</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>Force (F)</InfoLabel><InfoValue>{force} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Distance (d)</InfoLabel><InfoValue>{distM} m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Angle (&theta;)</InfoLabel><InfoValue>{forceAngle}&deg;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Work (W = Fd cos&theta;)</InfoLabel><InfoValue>{formatNum(workDone)} J</InfoValue></InfoItem>
        <InfoItem><InfoLabel>F cos&theta;</InfoLabel><InfoValue>{formatNum(horizontalComponent)} N</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        Push a box with a force at an angle. Work = F&middot;d&middot;cos&theta;. Only the component of force parallel to displacement does work. At &theta; = 0&deg;, all force does work. At 90&deg;, no work is done (force perpendicular to motion).
      </Instructions>
    </>
  );
};

/* ─── 16. Power ───────────────────────────────────────────────────────────── */

const PowerSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [mass, setMass] = useState(10);
  const [liftSpeed, setLiftSpeed] = useState(2);
  const [running, setRunning] = useState(false);
  const [liftHeight, setLiftHeight] = useState(0);
  const [simTime, setSimTime] = useState(0);

  const liftForce = mass * G;
  const power = liftForce * liftSpeed;
  const maxH = 5;

  const GROUND_Y = CANVAS_H - 60;
  const SCALE_Y = (CANVAS_H - 120) / maxH;

  const drawScene = useCallback(
    (h: number, t: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Ground
      ctx.fillStyle = '#eceff1';
      ctx.fillRect(0, GROUND_Y, CANVAS_W, CANVAS_H - GROUND_Y);
      ctx.strokeStyle = '#90a4ae';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y);
      ctx.lineTo(CANVAS_W, GROUND_Y);
      ctx.stroke();

      // Height scale
      const poleX = CANVAS_W / 2 - 80;
      ctx.strokeStyle = '#bbb';
      ctx.lineWidth = 1;
      ctx.fillStyle = '#999';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      for (let m = 0; m <= maxH; m++) {
        const sy = GROUND_Y - m * SCALE_Y;
        ctx.beginPath();
        ctx.moveTo(poleX - 5, sy);
        ctx.lineTo(poleX + 5, sy);
        ctx.stroke();
        ctx.fillText(`${m}m`, poleX - 8, sy + 3);
      }

      // Rope
      const boxCX = CANVAS_W / 2;
      const boxW = 50;
      const boxH = 40;
      const boxY = GROUND_Y - h * SCALE_Y - boxH;

      // Pulley at top
      ctx.fillStyle = '#78909c';
      ctx.beginPath();
      ctx.arc(boxCX, 40, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#546e7a';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Rope from pulley to box
      ctx.strokeStyle = '#8D6E63';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(boxCX, 52);
      ctx.lineTo(boxCX, boxY);
      ctx.stroke();

      // Box (being lifted)
      ctx.fillStyle = accentColor;
      ctx.fillRect(boxCX - boxW / 2, boxY, boxW, boxH);
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 2;
      ctx.strokeRect(boxCX - boxW / 2, boxY, boxW, boxH);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${mass} kg`, boxCX, boxY + boxH / 2 + 5);

      // Force arrow (up)
      const fLen = Math.min(liftForce * 0.5, 60);
      ctx.strokeStyle = '#4CAF50';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(boxCX + boxW / 2 + 15, boxY + boxH / 2);
      ctx.lineTo(boxCX + boxW / 2 + 15, boxY + boxH / 2 - fLen);
      ctx.stroke();
      ctx.fillStyle = '#4CAF50';
      ctx.beginPath();
      ctx.moveTo(boxCX + boxW / 2 + 15, boxY + boxH / 2 - fLen - 6);
      ctx.lineTo(boxCX + boxW / 2 + 10, boxY + boxH / 2 - fLen + 4);
      ctx.lineTo(boxCX + boxW / 2 + 20, boxY + boxH / 2 - fLen + 4);
      ctx.closePath();
      ctx.fill();
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`F = ${formatNum(liftForce, 0)} N`, boxCX + boxW / 2 + 22, boxY + boxH / 2 - fLen / 2);

      // Weight arrow (down)
      ctx.strokeStyle = '#F44336';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(boxCX - boxW / 2 - 15, boxY + boxH / 2);
      ctx.lineTo(boxCX - boxW / 2 - 15, boxY + boxH / 2 + fLen);
      ctx.stroke();
      ctx.fillStyle = '#F44336';
      ctx.beginPath();
      ctx.moveTo(boxCX - boxW / 2 - 15, boxY + boxH / 2 + fLen + 6);
      ctx.lineTo(boxCX - boxW / 2 - 20, boxY + boxH / 2 + fLen - 4);
      ctx.lineTo(boxCX - boxW / 2 - 10, boxY + boxH / 2 + fLen - 4);
      ctx.closePath();
      ctx.fill();
      ctx.textAlign = 'right';
      ctx.fillText(`mg = ${formatNum(liftForce, 0)} N`, boxCX - boxW / 2 - 22, boxY + boxH / 2 + fLen / 2);

      // Power display
      const workDone = liftForce * h;
      ctx.fillStyle = '#333';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`P = Fv = ${formatNum(liftForce, 0)} × ${liftSpeed} = ${formatNum(power, 0)} W`, CANVAS_W / 2, 25);
      if (t > 0) {
        ctx.font = '12px sans-serif';
        ctx.fillText(`W = Fd = ${formatNum(workDone, 0)} J  |  P = W/t = ${formatNum(t > 0 ? workDone / t : 0, 0)} W  |  h = ${formatNum(h, 1)} m`, CANVAS_W / 2, 48);
      }

      // Power meter (right side)
      const meterX = CANVAS_W - 80;
      const meterH = 200;
      const meterY = 70;
      const pMax = 500;
      const pFill = Math.min(power / pMax, 1) * meterH;

      ctx.fillStyle = '#eee';
      ctx.fillRect(meterX, meterY, 30, meterH);
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.strokeRect(meterX, meterY, 30, meterH);

      const pGrad = ctx.createLinearGradient(0, meterY + meterH, 0, meterY);
      pGrad.addColorStop(0, '#4CAF50');
      pGrad.addColorStop(0.5, '#FFC107');
      pGrad.addColorStop(1, '#F44336');
      ctx.fillStyle = pGrad;
      ctx.fillRect(meterX, meterY + meterH - pFill, 30, pFill);

      ctx.fillStyle = '#333';
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Power', meterX + 15, meterY - 5);
      ctx.fillText(`${formatNum(power, 0)} W`, meterX + 15, meterY + meterH + 15);
    },
    [mass, liftSpeed, liftForce, power, accentColor, maxH, SCALE_Y],
  );

  useEffect(() => { drawScene(0, 0); }, [drawScene]);

  const startSim = useCallback(() => {
    setRunning(true);
    setLiftHeight(0);
    setSimTime(0);
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      const simT = elapsed;
      const h = Math.min(liftSpeed * simT, maxH);
      setLiftHeight(h);
      setSimTime(simT);
      drawScene(h, simT);
      if (h < maxH) animRef.current = requestAnimationFrame(step);
      else setRunning(false);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [liftSpeed, maxH, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setLiftHeight(0);
    setSimTime(0);
    drawScene(0, 0);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Mass: {mass} kg</ControlLabel>
          <StyledSlider type="range" min={1} max={30} value={mass} onChange={(e) => { setMass(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Lift Speed: {liftSpeed} m/s</ControlLabel>
          <StyledSlider type="range" min={0.5} max={5} step={0.5} value={liftSpeed} onChange={(e) => { setLiftSpeed(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Lift</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>Force (mg)</InfoLabel><InfoValue>{formatNum(liftForce)} N</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Speed (v)</InfoLabel><InfoValue>{liftSpeed} m/s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Power (P = Fv)</InfoLabel><InfoValue>{formatNum(power)} W</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Work Done</InfoLabel><InfoValue>{formatNum(liftForce * liftHeight)} J</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Height</InfoLabel><InfoValue>{formatNum(liftHeight)} m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Time</InfoLabel><InfoValue>{formatNum(simTime)} s</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        Lift a mass at constant speed. The force needed equals mg (weight). Power = Force &times; velocity = Fv. More mass or faster lifting requires more power. Also P = W/t (work done divided by time).
      </Instructions>
    </>
  );
};

/* ─── 17. Torque ──────────────────────────────────────────────────────────── */

const TorqueSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [forceL, setForceL] = useState(20);
  const [distL, setDistL] = useState(2);
  const [forceR, setForceR] = useState(15);
  const [distR, setDistR] = useState(3);

  const torqueL = forceL * distL;
  const torqueR = forceR * distR;
  const netTorque = torqueR - torqueL;
  const balanced = Math.abs(netTorque) < 0.5;

  const PIVOT_X = CANVAS_W / 2;
  const PIVOT_Y = CANVAS_H / 2 + 20;
  const PX_PER_M = 50;
  const BEAM_H = 12;

  const drawScene = useCallback(
    () => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Calculate tilt angle based on net torque
      const tiltAngle = Math.max(-0.2, Math.min(0.2, netTorque * 0.005));

      ctx.save();
      ctx.translate(PIVOT_X, PIVOT_Y);
      ctx.rotate(tiltAngle);

      // Beam
      const beamLen = 5 * PX_PER_M;
      ctx.fillStyle = '#8D6E63';
      ctx.fillRect(-beamLen, -BEAM_H / 2, beamLen * 2, BEAM_H);
      ctx.strokeStyle = '#5D4037';
      ctx.lineWidth = 2;
      ctx.strokeRect(-beamLen, -BEAM_H / 2, beamLen * 2, BEAM_H);

      // Distance markers
      ctx.fillStyle = '#fff';
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'center';
      for (let m = -4; m <= 4; m++) {
        if (m === 0) continue;
        ctx.beginPath();
        ctx.moveTo(m * PX_PER_M, -BEAM_H / 2);
        ctx.lineTo(m * PX_PER_M, BEAM_H / 2);
        ctx.strokeStyle = 'rgba(255,255,255,0.5)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Left force (down, at distL from pivot)
      const lx = -distL * PX_PER_M;
      const lfLen = Math.min(forceL * 1.5, 80);
      ctx.strokeStyle = '#2196F3';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(lx, BEAM_H / 2);
      ctx.lineTo(lx, BEAM_H / 2 + lfLen);
      ctx.stroke();
      ctx.fillStyle = '#2196F3';
      ctx.beginPath();
      ctx.moveTo(lx, BEAM_H / 2 + lfLen + 6);
      ctx.lineTo(lx - 5, BEAM_H / 2 + lfLen - 4);
      ctx.lineTo(lx + 5, BEAM_H / 2 + lfLen - 4);
      ctx.closePath();
      ctx.fill();
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`F₁ = ${forceL} N`, lx, BEAM_H / 2 + lfLen + 20);
      ctx.fillText(`r₁ = ${distL} m`, lx, -BEAM_H / 2 - 8);

      // Right force (down, at distR from pivot)
      const rx = distR * PX_PER_M;
      const rfLen = Math.min(forceR * 1.5, 80);
      ctx.strokeStyle = '#F44336';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(rx, BEAM_H / 2);
      ctx.lineTo(rx, BEAM_H / 2 + rfLen);
      ctx.stroke();
      ctx.fillStyle = '#F44336';
      ctx.beginPath();
      ctx.moveTo(rx, BEAM_H / 2 + rfLen + 6);
      ctx.lineTo(rx - 5, BEAM_H / 2 + rfLen - 4);
      ctx.lineTo(rx + 5, BEAM_H / 2 + rfLen - 4);
      ctx.closePath();
      ctx.fill();
      ctx.font = 'bold 11px sans-serif';
      ctx.fillText(`F₂ = ${forceR} N`, rx, BEAM_H / 2 + rfLen + 20);
      ctx.fillText(`r₂ = ${distR} m`, rx, -BEAM_H / 2 - 8);

      // Torque arcs
      // Left torque (CCW)
      ctx.strokeStyle = '#2196F3';
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      const arcR1 = 30;
      ctx.beginPath();
      ctx.arc(0, 0, arcR1, Math.PI, Math.PI * 1.5);
      ctx.stroke();
      ctx.setLineDash([]);

      // Right torque (CW)
      ctx.strokeStyle = '#F44336';
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.arc(0, 0, arcR1 + 8, -Math.PI * 0.5, 0);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.restore();

      // Pivot triangle
      ctx.fillStyle = '#555';
      ctx.beginPath();
      ctx.moveTo(PIVOT_X, PIVOT_Y + 8);
      ctx.lineTo(PIVOT_X - 15, PIVOT_Y + 35);
      ctx.lineTo(PIVOT_X + 15, PIVOT_Y + 35);
      ctx.closePath();
      ctx.fill();

      // Base
      ctx.fillStyle = '#90a4ae';
      ctx.fillRect(PIVOT_X - 30, PIVOT_Y + 35, 60, 8);

      // Status
      ctx.fillStyle = '#333';
      ctx.font = 'bold 15px sans-serif';
      ctx.textAlign = 'center';
      if (balanced) {
        ctx.fillStyle = '#4CAF50';
        ctx.fillText('BALANCED! Net torque ≈ 0', CANVAS_W / 2, 25);
      } else {
        ctx.fillStyle = netTorque > 0 ? '#F44336' : '#2196F3';
        ctx.fillText(`Net torque = ${formatNum(netTorque, 1)} N·m (${netTorque > 0 ? 'clockwise' : 'counter-clockwise'})`, CANVAS_W / 2, 25);
      }

      ctx.fillStyle = '#333';
      ctx.font = '12px sans-serif';
      ctx.fillText(`τ₁ = F₁×r₁ = ${formatNum(torqueL, 0)} N·m (CCW)    τ₂ = F₂×r₂ = ${formatNum(torqueR, 0)} N·m (CW)`, CANVAS_W / 2, 48);
    },
    [forceL, distL, forceR, distR, torqueL, torqueR, netTorque, balanced],
  );

  useEffect(() => { drawScene(); }, [drawScene]);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Left Force (F₁): {forceL} N</ControlLabel>
          <StyledSlider type="range" min={5} max={50} value={forceL} onChange={(e) => setForceL(+e.target.value)} $accent="#2196F3" />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Left Distance (r₁): {distL} m</ControlLabel>
          <StyledSlider type="range" min={0.5} max={4} step={0.5} value={distL} onChange={(e) => setDistL(+e.target.value)} $accent="#2196F3" />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Right Force (F₂): {forceR} N</ControlLabel>
          <StyledSlider type="range" min={5} max={50} value={forceR} onChange={(e) => setForceR(+e.target.value)} $accent="#F44336" />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Right Distance (r₂): {distR} m</ControlLabel>
          <StyledSlider type="range" min={0.5} max={4} step={0.5} value={distR} onChange={(e) => setDistR(+e.target.value)} $accent="#F44336" />
        </ControlRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>&tau;₁ = F₁r₁</InfoLabel><InfoValue>{formatNum(torqueL)} N&middot;m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>&tau;₂ = F₂r₂</InfoLabel><InfoValue>{formatNum(torqueR)} N&middot;m</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Net Torque</InfoLabel><InfoValue>{formatNum(netTorque)} N&middot;m</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        Torque = Force &times; lever arm distance (&tau; = rF). Adjust the forces and distances on each side of the beam. When the torques balance (&tau;₁ = &tau;₂), the beam is in rotational equilibrium. The beam tilts toward the side with greater torque.
      </Instructions>
    </>
  );
};

/* ─── 18. Angular Motion ──────────────────────────────────────────────────── */

const AngularMotionSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [omega0, setOmega0] = useState(2);
  const [alpha, setAlpha] = useState(0.5);
  const [running, setRunning] = useState(false);
  const [simTime, setSimTime] = useState(0);
  const [curTheta, setCurTheta] = useState(0);
  const [curOmega, setCurOmega] = useState(2);

  const CX = CANVAS_W / 2;
  const CY = CANVAS_H / 2 + 20;
  const WHEEL_R = 100;

  const drawScene = useCallback(
    (thetaRad: number, omegaVal: number, t: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Wheel
      ctx.strokeStyle = '#ddd';
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.arc(CX, CY, WHEEL_R, 0, Math.PI * 2);
      ctx.stroke();

      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.arc(CX, CY, WHEEL_R, 0, Math.PI * 2);
      ctx.stroke();

      // Spokes
      for (let i = 0; i < 6; i++) {
        const spokeAngle = thetaRad + (i * Math.PI * 2) / 6;
        ctx.strokeStyle = '#999';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(CX, CY);
        ctx.lineTo(CX + WHEEL_R * 0.9 * Math.cos(spokeAngle), CY + WHEEL_R * 0.9 * Math.sin(spokeAngle));
        ctx.stroke();
      }

      // Reference mark on rim
      const markAngle = thetaRad;
      const mx = CX + WHEEL_R * Math.cos(markAngle);
      const my = CY + WHEEL_R * Math.sin(markAngle);
      ctx.fillStyle = '#F44336';
      ctx.beginPath();
      ctx.arc(mx, my, 8, 0, Math.PI * 2);
      ctx.fill();

      // Center
      ctx.fillStyle = '#555';
      ctx.beginPath();
      ctx.arc(CX, CY, 6, 0, Math.PI * 2);
      ctx.fill();

      // Angular velocity arrow (curved)
      if (Math.abs(omegaVal) > 0.1) {
        const arrowR = WHEEL_R + 25;
        const arcLen = Math.min(Math.abs(omegaVal) * 0.3, 1.2);
        const startA = -Math.PI / 4;
        const endA = startA + (omegaVal > 0 ? arcLen : -arcLen);
        ctx.strokeStyle = '#4CAF50';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(CX, CY, arrowR, Math.min(startA, endA), Math.max(startA, endA));
        ctx.stroke();
        // Arrow tip
        const tipAngle = endA;
        const tipX = CX + arrowR * Math.cos(tipAngle);
        const tipY = CY + arrowR * Math.sin(tipAngle);
        ctx.fillStyle = '#4CAF50';
        ctx.beginPath();
        ctx.arc(tipX, tipY, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`ω = ${formatNum(omegaVal, 2)} rad/s`, CX, CY - WHEEL_R - 35);
      }

      // Info display
      const revolutions = thetaRad / (2 * Math.PI);
      ctx.fillStyle = '#333';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`θ = ${formatNum(thetaRad, 2)} rad (${formatNum(revolutions, 1)} rev)`, CANVAS_W / 2, 25);
      ctx.font = '12px sans-serif';
      ctx.fillText(`ω = ω₀ + αt = ${formatNum(omega0, 1)} + ${formatNum(alpha, 1)}×${formatNum(t, 1)} = ${formatNum(omegaVal, 2)} rad/s`, CANVAS_W / 2, 48);
    },
    [omega0, alpha, accentColor],
  );

  useEffect(() => { drawScene(0, omega0, 0); }, [drawScene, omega0]);

  const startSim = useCallback(() => {
    setRunning(true);
    setSimTime(0);
    setCurTheta(0);
    setCurOmega(omega0);
    let startTime: number | null = null;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      const t = elapsed;
      const w = omega0 + alpha * t;
      const th = omega0 * t + 0.5 * alpha * t * t;
      setCurTheta(th);
      setCurOmega(w);
      setSimTime(t);
      drawScene(th, w, t);
      if (t < 8 && w > -10 && w < 20) animRef.current = requestAnimationFrame(step);
      else setRunning(false);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [omega0, alpha, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setSimTime(0);
    setCurTheta(0);
    setCurOmega(omega0);
    drawScene(0, omega0, 0);
  }, [omega0, drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Initial Angular Velocity (&omega;₀): {omega0} rad/s</ControlLabel>
          <StyledSlider type="range" min={0} max={6} step={0.5} value={omega0} onChange={(e) => { setOmega0(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ControlRow>
          <ControlLabel>Angular Acceleration (&alpha;): {alpha} rad/s&sup2;</ControlLabel>
          <StyledSlider type="range" min={-2} max={3} step={0.25} value={alpha} onChange={(e) => { setAlpha(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Spin</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>&theta; (angle)</InfoLabel><InfoValue>{formatNum(curTheta)} rad</InfoValue></InfoItem>
        <InfoItem><InfoLabel>&omega; (ang. vel.)</InfoLabel><InfoValue>{formatNum(curOmega)} rad/s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>&alpha; (ang. accel.)</InfoLabel><InfoValue>{formatNum(alpha)} rad/s&sup2;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Revolutions</InfoLabel><InfoValue>{formatNum(curTheta / (2 * Math.PI), 1)}</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Time</InfoLabel><InfoValue>{formatNum(simTime)} s</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        A spinning wheel shows angular quantities: &theta; (angular displacement), &omega; (angular velocity), and &alpha; (angular acceleration). Just like linear kinematics: &omega; = &omega;₀ + &alpha;t and &theta; = &omega;₀t + ½&alpha;t². The red dot tracks the wheel's rotation.
      </Instructions>
    </>
  );
};

/* ─── 19. Rotational Inertia (Rolling Race) ───────────────────────────────── */

const RotationalInertiaSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [rampAngle, setRampAngle] = useState(25);
  const [running, setRunning] = useState(false);
  const [, setPos1] = useState(0);
  const [, setPos2] = useState(0);

  // Solid cylinder: I = ½MR², a = 2/3 g sin θ
  // Hollow cylinder: I = MR², a = 1/2 g sin θ
  const sinTheta = Math.sin(toRad(rampAngle));
  const accSolid = (2 / 3) * G * sinTheta;
  const accHollow = (1 / 2) * G * sinTheta;

  const RAMP_LEFT = 60;
  const RAMP_RIGHT = CANVAS_W - 60;
  const RAMP_TOP = 80;
  const RAMP_BOT = CANVAS_H - 80;

  const drawScene = useCallback(
    (p1: number, p2: number) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      const rampLen = Math.sqrt((RAMP_RIGHT - RAMP_LEFT) ** 2 + (RAMP_BOT - RAMP_TOP) ** 2);
      const dx = (RAMP_RIGHT - RAMP_LEFT) / rampLen;
      const dy = (RAMP_BOT - RAMP_TOP) / rampLen;

      // Ramp surface
      ctx.fillStyle = '#d7ccc8';
      ctx.beginPath();
      ctx.moveTo(RAMP_LEFT, RAMP_TOP);
      ctx.lineTo(RAMP_RIGHT, RAMP_BOT);
      ctx.lineTo(RAMP_RIGHT, RAMP_BOT + 10);
      ctx.lineTo(RAMP_LEFT, RAMP_TOP + 10);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#8D6E63';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(RAMP_LEFT, RAMP_TOP);
      ctx.lineTo(RAMP_RIGHT, RAMP_BOT);
      ctx.stroke();

      // Ground
      ctx.fillStyle = '#e0e0e0';
      ctx.fillRect(0, RAMP_BOT + 10, CANVAS_W, CANVAS_H - RAMP_BOT - 10);

      // Object 1: Solid cylinder (faster) - lane 1
      const lane1Offset = -20;
      const obj1X = RAMP_LEFT + p1 * dx + lane1Offset * dy;
      const obj1Y = RAMP_TOP + p1 * dy - lane1Offset * dx;
      const r1 = 15;

      ctx.fillStyle = '#2196F3';
      ctx.beginPath();
      ctx.arc(Math.min(obj1X, RAMP_RIGHT), Math.min(obj1Y, RAMP_BOT) - r1, r1, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#1565C0';
      ctx.lineWidth = 2;
      ctx.stroke();
      // Solid fill indicator
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.beginPath();
      ctx.arc(Math.min(obj1X, RAMP_RIGHT), Math.min(obj1Y, RAMP_BOT) - r1, r1 * 0.5, 0, Math.PI * 2);
      ctx.fill();

      // Object 2: Hollow cylinder (slower) - lane 2
      const lane2Offset = 20;
      const obj2X = RAMP_LEFT + p2 * dx + lane2Offset * dy;
      const obj2Y = RAMP_TOP + p2 * dy - lane2Offset * dx;
      const r2 = 15;

      ctx.strokeStyle = '#F44336';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(Math.min(obj2X, RAMP_RIGHT), Math.min(obj2Y, RAMP_BOT) - r2, r2, 0, Math.PI * 2);
      ctx.stroke();
      // Hollow indicator (ring only)
      ctx.strokeStyle = '#C62828';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(Math.min(obj2X, RAMP_RIGHT), Math.min(obj2Y, RAMP_BOT) - r2, r2 * 0.7, 0, Math.PI * 2);
      ctx.stroke();

      // Labels
      ctx.fillStyle = '#2196F3';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('Solid Cylinder (I = ½MR²)', 15, 25);
      ctx.fillText(`a = ²⁄₃ g sinθ = ${formatNum(accSolid, 2)} m/s²`, 15, 42);

      ctx.fillStyle = '#F44336';
      ctx.fillText('Hollow Cylinder (I = MR²)', 15, 65);
      ctx.fillText(`a = ½ g sinθ = ${formatNum(accHollow, 2)} m/s²`, 15, 82);

      // Winner label
      ctx.fillStyle = '#333';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`Ramp angle: ${rampAngle}° — Solid always wins (less rotational inertia ratio)!`, CANVAS_W / 2, CANVAS_H - 20);

      // Angle label
      ctx.fillStyle = '#FF9800';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`θ = ${rampAngle}°`, RAMP_LEFT + 10, RAMP_TOP - 10);
    },
    [rampAngle, accSolid, accHollow],
  );

  useEffect(() => { drawScene(0, 0); }, [drawScene]);

  const startSim = useCallback(() => {
    setRunning(true);
    setPos1(0);
    setPos2(0);
    let startTime: number | null = null;
    const rampLen = Math.sqrt((RAMP_RIGHT - RAMP_LEFT) ** 2 + (RAMP_BOT - RAMP_TOP) ** 2);

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      const t = elapsed * 0.8;
      const p1 = Math.min(0.5 * accSolid * t * t * 15, rampLen);
      const p2 = Math.min(0.5 * accHollow * t * t * 15, rampLen);
      setPos1(p1);
      setPos2(p2);
      drawScene(p1, p2);
      if (p1 < rampLen && t < 6) animRef.current = requestAnimationFrame(step);
      else setRunning(false);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [accSolid, accHollow, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setPos1(0);
    setPos2(0);
    drawScene(0, 0);
  }, [drawScene]);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Ramp Angle: {rampAngle}&deg;</ControlLabel>
          <StyledSlider type="range" min={10} max={50} value={rampAngle} onChange={(e) => { setRampAngle(+e.target.value); if (!running) resetSim(); }} $accent={accentColor} />
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Race!</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>Solid Accel</InfoLabel><InfoValue>{formatNum(accSolid)} m/s&sup2;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Hollow Accel</InfoLabel><InfoValue>{formatNum(accHollow)} m/s&sup2;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Ramp Angle</InfoLabel><InfoValue>{rampAngle}&deg;</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        A solid and hollow cylinder of the same mass and radius race down a ramp. The solid cylinder always wins because it has less rotational inertia relative to its mass (I = ½MR² vs MR²). More rotational inertia means more energy goes into spinning rather than translating. &tau; = I&alpha; governs each object's angular acceleration.
      </Instructions>
    </>
  );
};

/* ─── 20. Angular Momentum (Ice Skater) ───────────────────────────────────── */

const AngularMomentumSim: React.FC<{ accentColor: string }> = ({ accentColor }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const [armsOut, setArmsOut] = useState(true);
  const [running, setRunning] = useState(false);
  const [theta, setTheta] = useState(0);

  // Arms out: I_big, omega_slow; Arms in: I_small, omega_fast
  const I_out = 5;
  const I_in = 1.5;
  const omega_out = 2;
  const L = I_out * omega_out; // conserved
  const omega_in = L / I_in;

  const currentI = armsOut ? I_out : I_in;
  const currentOmega = armsOut ? omega_out : omega_in;

  const CX = CANVAS_W / 2;
  const CY = CANVAS_H / 2 + 20;

  const drawScene = useCallback(
    (angle: number, isArmsOut: boolean) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#e8eaf6';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Ice rink (circle)
      ctx.fillStyle = '#e3f2fd';
      ctx.beginPath();
      ctx.arc(CX, CY, 130, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#90CAF9';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.save();
      ctx.translate(CX, CY);
      ctx.rotate(angle);

      // Body (ellipse)
      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.ellipse(0, 0, 12, 30, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Head
      ctx.fillStyle = '#FFCCBC';
      ctx.beginPath();
      ctx.arc(0, -35, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.1)';
      ctx.stroke();

      // Arms
      const armLen = isArmsOut ? 50 : 15;
      const armY = -10;
      ctx.strokeStyle = '#FFCCBC';
      ctx.lineWidth = 4;
      // Left arm
      ctx.beginPath();
      ctx.moveTo(-12, armY);
      ctx.lineTo(-12 - armLen, armY);
      ctx.stroke();
      // Right arm
      ctx.beginPath();
      ctx.moveTo(12, armY);
      ctx.lineTo(12 + armLen, armY);
      ctx.stroke();

      // Hands
      ctx.fillStyle = '#FFCCBC';
      ctx.beginPath();
      ctx.arc(-12 - armLen, armY, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(12 + armLen, armY, 4, 0, Math.PI * 2);
      ctx.fill();

      // Legs
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(-5, 28);
      ctx.lineTo(-8, 50);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(5, 28);
      ctx.lineTo(8, 50);
      ctx.stroke();

      ctx.restore();

      // Rotation indicator (curved arrow around skater)
      const omegaNow = isArmsOut ? omega_out : omega_in;
      const arcR = 80;
      const arcLen = Math.min(omegaNow * 0.2, 1.5);
      ctx.strokeStyle = '#FF9800';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(CX, CY, arcR, -0.5, -0.5 + arcLen);
      ctx.stroke();
      // Arrowhead
      const tipAngle = -0.5 + arcLen;
      const tipX = CX + arcR * Math.cos(tipAngle);
      const tipY = CY + arcR * Math.sin(tipAngle);
      ctx.fillStyle = '#FF9800';
      ctx.beginPath();
      ctx.arc(tipX, tipY, 5, 0, Math.PI * 2);
      ctx.fill();

      // Info
      ctx.fillStyle = '#333';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`L = Iω = ${formatNum(L, 1)} kg·m²/s (CONSERVED)`, CANVAS_W / 2, 20);
      ctx.font = '13px sans-serif';
      const iNow = isArmsOut ? I_out : I_in;
      ctx.fillText(`I = ${formatNum(iNow, 1)} kg·m²  |  ω = ${formatNum(omegaNow, 1)} rad/s`, CANVAS_W / 2, 42);

      // Side comparison
      ctx.fillStyle = '#2196F3';
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`Arms OUT: I = ${I_out}, ω = ${formatNum(omega_out, 1)} rad/s`, 15, CANVAS_H - 40);
      ctx.fillStyle = '#F44336';
      ctx.fillText(`Arms IN:  I = ${I_in}, ω = ${formatNum(omega_in, 1)} rad/s`, 15, CANVAS_H - 20);
      ctx.fillStyle = '#4CAF50';
      ctx.fillText(`L = Iω = ${formatNum(L, 1)} (same!)`, 350, CANVAS_H - 30);
    },
    [armsOut, accentColor, L, omega_out, omega_in],
  );

  useEffect(() => { drawScene(theta, armsOut); }, [drawScene, theta, armsOut]);

  const startSim = useCallback(() => {
    setRunning(true);
    let lastTimestamp: number | null = null;
    let angle = 0;

    const step = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const dt = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;
      angle += currentOmega * dt;
      setTheta(angle);
      drawScene(angle, armsOut);
      if (angle < Math.PI * 12) animRef.current = requestAnimationFrame(step);
      else setRunning(false);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);
  }, [currentOmega, armsOut, drawScene]);

  const resetSim = useCallback(() => {
    cancelAnimationFrame(animRef.current);
    setRunning(false);
    setTheta(0);
    drawScene(0, armsOut);
  }, [armsOut, drawScene]);



  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  return (
    <>
      <CanvasWrapper><StyledCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} /></CanvasWrapper>
      <ControlsPanel>
        <ControlRow>
          <ControlLabel>Arms Position:</ControlLabel>
          <ToggleRow>
            <ToggleButton $active={armsOut} $accent={accentColor} onClick={() => { setArmsOut(true); }}>Arms Out (slow)</ToggleButton>
            <ToggleButton $active={!armsOut} $accent={accentColor} onClick={() => { setArmsOut(false); }}>Arms In (fast)</ToggleButton>
          </ToggleRow>
        </ControlRow>
        <ButtonRow>
          <SimButton $accent={accentColor} onClick={startSim} disabled={running}>Spin</SimButton>
          <SimButtonOutline $accent={accentColor} onClick={resetSim}>Reset</SimButtonOutline>
        </ButtonRow>
      </ControlsPanel>
      <InfoPanel>
        <InfoItem><InfoLabel>Angular Momentum (L)</InfoLabel><InfoValue>{formatNum(L)} kg&middot;m&sup2;/s</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Moment of Inertia (I)</InfoLabel><InfoValue>{formatNum(currentI)} kg&middot;m&sup2;</InfoValue></InfoItem>
        <InfoItem><InfoLabel>Angular Velocity (&omega;)</InfoLabel><InfoValue>{formatNum(currentOmega)} rad/s</InfoValue></InfoItem>
      </InfoPanel>
      <Instructions>
        An ice skater demonstrates conservation of angular momentum (L = I&omega;). When arms are pulled in, rotational inertia decreases and angular velocity increases to keep L constant. Toggle between arms out and arms in while spinning to see the effect!
      </Instructions>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  MAIN SIMULATION SCREEN                                                   */
/* ═══════════════════════════════════════════════════════════════════════════ */

const SIMULATION_MAP: Record<string, React.FC<{ accentColor: string }>> = {
  // Unit 1: Kinematics
  'position-displacement': MotionSim,
  'velocity-speed': MotionSim,
  'acceleration': FreeFallSim,
  'kinematic-equations': ProjectileMotionSim,
  'free-fall': FreeFallSim,
  'projectile-motion': ProjectileMotionSim,
  'motion-graphs': MotionGraphsSim,
  // Unit 2: Dynamics
  'newtons-first-law': FrictionSim,
  'newtons-second-law': NewtonsSecondLawSim,
  'newtons-third-law': CollisionsSim,
  'free-body-diagrams': FrictionSim,
  'friction': FrictionSim,
  'tension-normal': TensionAtwoodSim,
  'inclined-planes': InclinedPlaneSim,
  // Unit 3: Circular Motion & Gravitation
  'uniform-circular-motion': CircularMotionSim,
  'centripetal-force': CircularMotionSim,
  'gravitational-force': GravityOrbitSim,
  'orbits': GravityOrbitSim,
  // Unit 4: Energy
  'work': WorkSim,
  'kinetic-energy': ConservationOfEnergySim,
  'potential-energy': ConservationOfEnergySim,
  'conservation-of-energy': ConservationOfEnergySim,
  'power': PowerSim,
  'work-energy-theorem': ConservationOfEnergySim,
  // Unit 5: Momentum
  'linear-momentum': CollisionsSim,
  'impulse': CollisionsSim,
  'conservation-of-momentum': CollisionsSim,
  'collisions': CollisionsSim,
  // Unit 6: Simple Harmonic Motion
  'oscillations': SpringsSim,
  'springs': SpringsSim,
  'pendulums': PendulumsSim,
  'energy-in-shm': SpringsSim,
  // Unit 7: Torque & Rotational Motion
  'angular-quantities': AngularMotionSim,
  'torque': TorqueSim,
  'rotational-inertia': RotationalInertiaSim,
  'rotational-dynamics': RotationalInertiaSim,
  'angular-momentum': AngularMomentumSim,
};

const SimulationScreen: React.FC = () => {
  const { unitId, conceptId } = useParams<{ unitId: string; conceptId: string }>();
  const navigate = useNavigate();

  const unit = unitId ? getUnit(unitId) : undefined;
  const concept = unitId && conceptId ? getConcept(unitId, conceptId) : undefined;
  const accentColor = unit?.color || '#2196F3';

  const SimComponent = conceptId ? SIMULATION_MAP[conceptId] : undefined;

  const [guidedMode, setGuidedMode] = useState(false);
  const [guidedStep, setGuidedStep] = useState(0);
  const guide = conceptId ? SIMULATION_GUIDE[conceptId] : undefined;

  useEffect(() => {
    setGuidedMode(false);
    setGuidedStep(0);
  }, [conceptId]);

  return (
    <Container>
      <Header $color={accentColor}>
        <BackButton onClick={() => navigate(`/unit/${unitId}`)}>
          <BackArrow>&larr;</BackArrow>
          <span>Back to {unit?.title || 'Unit'}</span>
        </BackButton>
        <HeaderTitle>
          <HeaderIcon>&#x1F52C;</HeaderIcon>
          <HeaderText>
            <ConceptTitle>{concept?.title || 'Simulation'}</ConceptTitle>
            <ConceptSub>Interactive Simulation</ConceptSub>
          </HeaderText>
        </HeaderTitle>
      </Header>

      <Main>
        <SimCard>
          {SimComponent ? (
            <>
              {/* Key Idea Banner */}
              {guide && (
                <KeyIdeaBanner $color={accentColor}>
                  <KeyIdeaIcon>💡</KeyIdeaIcon>
                  <KeyIdeaText>{guide.keyIdea}</KeyIdeaText>
                </KeyIdeaBanner>
              )}

              {/* The actual simulation */}
              <SimComponent accentColor={accentColor} />

              {/* Concept-specific instructions (override generic) */}
              {guide && (
                <Instructions>
                  {guide.instructions}
                </Instructions>
              )}

              {/* Guided Mode Section */}
              {guide && guide.guidedSteps.length > 0 && (
                <GuidedSection $color={accentColor}>
                  <GuidedHeader>
                    <GuidedTitle>
                      <span>🎯</span> Guided Exploration
                    </GuidedTitle>
                    <GuidedToggle
                      $active={guidedMode}
                      $accent={accentColor}
                      onClick={() => { setGuidedMode(!guidedMode); setGuidedStep(0); }}
                    >
                      {guidedMode ? 'Exit Guide' : 'Start Guide'}
                    </GuidedToggle>
                  </GuidedHeader>

                  {guidedMode && (
                    <GuidedContent>
                      <GuidedStepIndicator>
                        Step {guidedStep + 1} of {guide.guidedSteps.length}
                      </GuidedStepIndicator>
                      <GuidedStepCard $color={accentColor}>
                        <GuidedInstruction>
                          <strong>Do this:</strong> {guide.guidedSteps[guidedStep].instruction}
                        </GuidedInstruction>
                        <GuidedObserve>
                          <strong>Notice:</strong> {guide.guidedSteps[guidedStep].observe}
                        </GuidedObserve>
                      </GuidedStepCard>
                      <GuidedNav>
                        <SimButtonOutline
                          $accent={accentColor}
                          onClick={() => setGuidedStep(Math.max(0, guidedStep - 1))}
                          disabled={guidedStep === 0}
                        >
                          ← Previous
                        </SimButtonOutline>
                        <SimButton
                          $accent={accentColor}
                          onClick={() => setGuidedStep(Math.min(guide.guidedSteps.length - 1, guidedStep + 1))}
                          disabled={guidedStep === guide.guidedSteps.length - 1}
                        >
                          Next Step →
                        </SimButton>
                      </GuidedNav>
                    </GuidedContent>
                  )}
                </GuidedSection>
              )}

              {/* What to Try Section */}
              {guide && guide.whatToTry.length > 0 && (
                <WhatToTrySection>
                  <WhatToTryTitle>🧪 What to Try</WhatToTryTitle>
                  <WhatToTryList>
                    {guide.whatToTry.map((item, i) => (
                      <WhatToTryItem key={i}>
                        <WhatToTryNumber $color={accentColor}>{i + 1}</WhatToTryNumber>
                        <span>{item}</span>
                      </WhatToTryItem>
                    ))}
                  </WhatToTryList>
                </WhatToTrySection>
              )}
            </>
          ) : (
            <ComingSoon>
              <ComingSoonIcon>&#x1F6A7;</ComingSoonIcon>
              <ComingSoonTitle>Simulation Coming Soon!</ComingSoonTitle>
              <ComingSoonText>
                An interactive simulation for <strong>{concept?.title || 'this concept'}</strong> is
                currently in development. Check back soon!
              </ComingSoonText>
              <SimButton
                $accent={accentColor}
                onClick={() => navigate(`/unit/${unitId}`)}
                style={{ marginTop: 16 }}
              >
                Back to {unit?.title || 'Unit'}
              </SimButton>
            </ComingSoon>
          )}
        </SimCard>
      </Main>
    </Container>
  );
};

export default SimulationScreen;

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  STYLED COMPONENTS                                                        */
/* ═══════════════════════════════════════════════════════════════════════════ */

const KeyIdeaBanner = styled.div<{ $color: string }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: linear-gradient(135deg, ${props => props.$color}12, ${props => props.$color}20);
  border: 1px solid ${props => props.$color}35;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
`;

const KeyIdeaIcon = styled.span`
  font-size: 24px;
  line-height: 1;
  flex-shrink: 0;
`;

const KeyIdeaText = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.5;
`;

const GuidedSection = styled.div<{ $color: string }>`
  background: #fff;
  border: 2px solid ${props => props.$color}30;
  border-radius: 14px;
  padding: 20px;
  margin-top: 20px;
`;

const GuidedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const GuidedTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GuidedToggle = styled.button<{ $active: boolean; $accent: string }>`
  background: ${props => props.$active ? props.$accent : 'transparent'};
  color: ${props => props.$active ? '#fff' : props.$accent};
  border: 2px solid ${props => props.$accent};
  border-radius: 8px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? props.$accent : props.$accent + '15'};
  }
`;

const GuidedContent = styled.div`
  margin-top: 16px;
`;

const GuidedStepIndicator = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #8890a4;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const GuidedStepCard = styled.div<{ $color: string }>`
  background: ${props => props.$color}08;
  border-left: 4px solid ${props => props.$color};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 14px;
`;

const GuidedInstruction = styled.p`
  font-size: 14px;
  color: #1a1a2e;
  margin: 0 0 8px;
  line-height: 1.5;
`;

const GuidedObserve = styled.p`
  font-size: 13px;
  color: #5f6577;
  margin: 0;
  line-height: 1.5;
  font-style: italic;
`;

const GuidedNav = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

const WhatToTrySection = styled.div`
  background: #f8fdf8;
  border: 1px solid #c8e6c9;
  border-radius: 14px;
  padding: 20px;
  margin-top: 20px;
`;

const WhatToTryTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #2e7d32;
  margin: 0 0 14px;
`;

const WhatToTryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WhatToTryItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  color: #37474f;
  line-height: 1.5;
`;

const WhatToTryNumber = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.$color};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const Container = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`;

const Header = styled.header<{ $color: string }>`
  background: ${(props) => props.$color};
  padding: 20px 24px 24px;
  color: #fff;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  padding: 6px 14px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-bottom: 16px;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const BackArrow = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const HeaderIcon = styled.span`
  font-size: 32px;
  line-height: 1;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ConceptTitle = styled.h1`
  font-size: 24px;
  font-weight: 800;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

const ConceptSub = styled.span`
  font-size: 14px;
  font-weight: 400;
  opacity: 0.85;
`;

const Main = styled.main`
  max-width: 900px;
  margin: -12px auto 0;
  padding: 0 24px 60px;
  position: relative;
  z-index: 2;
`;

const SimCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04);
  padding: 24px;
  overflow: hidden;
`;

const CanvasWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: #fafafa;
`;

const StyledCanvas = styled.canvas`
  display: block;
  max-width: 100%;
  height: auto;
`;

const ControlsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #f8f9fc;
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 16px;
`;

const ControlRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ControlLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
`;

const StyledSlider = styled.input<{ $accent: string }>`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #ddd;
  outline: none;
  transition: background 0.2s;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${(props) => props.$accent};
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${(props) => props.$accent};
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const SimButton = styled.button<{ $accent: string }>`
  background: ${(props) => props.$accent};
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SimButtonOutline = styled.button<{ $accent: string }>`
  background: transparent;
  color: ${(props) => props.$accent};
  border: 2px solid ${(props) => props.$accent};
  border-radius: 10px;
  padding: 8px 22px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;

  &:hover {
    background: ${(props) => props.$accent}11;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  gap: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #ddd;
`;

const ToggleButton = styled.button<{ $active: boolean; $accent: string }>`
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  background: ${(props) => (props.$active ? props.$accent : '#f5f5f5')};
  color: ${(props) => (props.$active ? '#fff' : '#666')};
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: ${(props) => (props.$active ? props.$accent : '#e0e0e0')};
  }
`;

const InfoPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  background: #f8f9fc;
  border-radius: 8px;
  padding: 10px 14px;
  gap: 8px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #8890a4;
  white-space: nowrap;
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
  font-variant-numeric: tabular-nums;
`;

const Instructions = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: #5f6577;
  background: #f0f4f8;
  border-radius: 10px;
  padding: 14px 18px;
  border-left: 4px solid #90a4ae;
`;

const ComingSoon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
`;

const ComingSoonIcon = styled.span`
  font-size: 64px;
  line-height: 1;
  margin-bottom: 16px;
`;

const ComingSoonTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px;
`;

const ComingSoonText = styled.p`
  font-size: 15px;
  color: #5f6577;
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
`;
